// src/components/Dashboard.js

// ============= Imports =============
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { SwapHoriz as MoveIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import FileGrid from './FileGrid';
import CanvasTimelineView from './CanvasTimelineView';
import JsonPreview from './JsonPreview';
import DeltaPreview from './DeltaPreview';
import BrowserTimelapse from './BrowserTimelapse';
import { Slider, FormControl, Chip, Paper, FormControlLabel, Switch, Radio, RadioGroup, ToggleButton, ToggleButtonGroup, Tooltip, useMediaQuery, useTheme } from '@mui/material';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  AppBar,
  Toolbar,
  IconButton,
  CircularProgress,
  Alert,
  LinearProgress,
  Snackbar,
  Fade,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  Folder as FolderIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
  AccountCircle as AccountCircleIcon,
  Edit as EditIcon,
  VideoLibrary as VideoLibraryIcon,
  Videocam as VideoIcon,
  Archive as ArchiveIcon,
  Unarchive as UnarchiveIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Menu as MenuIcon,
  Visibility as VisibilityIcon,
  LiveHelp as LiveHelpIcon,
  Info as InfoIcon,
  ViewList as ViewListIcon,
  ViewModule as ViewModuleIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { operationService, OPERATION_LIMITS, OperationType } from '../services/operationService';
import VideoPreview from './VideoPreview';
import GuidedTour from './GuidedTour';
import TourButton from './TourButton';
import { useTour } from '../contexts/TourContext';
import { getDashboardTourSteps } from '../utils/tourSteps';
import { getGroupColor, getGroupColors } from '../utils/colorPalette';

// Helper function for auth - standalone mode uses cookies (no token needed)
const getAuthToken = () => {
  // Standalone mode: authentication handled by cookies sent with withCredentials: true
  // No need for Bearer token headers
  return null;
};

// ============= Operation Progress Component =============
// This component is used to display progress for file operations
const OperationProgress = ({ operation }) => {
  if (!operation) return null;

  // Use upload progress if available, otherwise calculate from completed/total
  const progress = operation.uploadProgress !== undefined
    ? operation.uploadProgress
    : (operation.completed / operation.total) * 100;

  let operationMessage = 'Processing files...';
  if (operation.type === 'move') {
    operationMessage = `Moving files (Batch ${operation.currentBatch || 1}/${operation.totalBatches || 1})...`;
  } else if (operation.type === 'delete') {
    operationMessage = `Deleting files (Batch ${operation.currentBatch || 1}/${operation.totalBatches || 1})...`;
  } else if (operation.type === 'download') {
    operationMessage = 'Downloading files...';
  } else if (operation.type === 'upload_to_gallery') {
    if (operation.currentFileName) {
      operationMessage = `Uploading "${operation.currentFileName}" (${operation.currentFile || 1}/${operation.total})...`;
    } else {
      operationMessage = `Uploading files to gallery (${operation.completed}/${operation.total})...`;
    }
  }

  return (
    <Box 
      sx={{ 
        width: '100%', 
        mb: 2, 
        p: 2, 
        borderRadius: 2, 
        backgroundColor: 'background.card',
        boxShadow: 2
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="body1" fontWeight={500} color="primary.light">
          {operationMessage}
        </Typography>
        <Typography variant="body2" fontWeight={500} color="text.secondary">
          {operation.uploadProgress !== undefined
            ? `${Math.round(progress)}%`
            : `${operation.completed} / ${operation.total}`
          }
        </Typography>
      </Box>
      <LinearProgress
        variant="determinate"
        value={progress}
        sx={{
          height: 8,
          borderRadius: 4,
          backgroundColor: 'rgba(34, 211, 238, 0.1)',
          '& .MuiLinearProgress-bar': {
            borderRadius: 4,
            boxShadow: '0 0 10px rgba(34, 211, 238, 0.3)',
          }
        }}
      />
    </Box>
  );
};

// ============= Utility Functions =============
/**
 * Gets document name from file metadata or returns a formatted version of the filename
 * @param {Object} file - File object
 * @returns {string} Document name
 */
const getDocumentName = (file) => {
  if (file.metadata) {
    if (file.metadata['document-name']) return file.metadata['document-name'];
    if (file.metadata.document_name) return file.metadata.document_name;
    
    // For SuprArtData files, try to extract a nice name from timestamp
    const timestampMatch = file.name.match(/SuprArtData-(\d{13})/);
    if (timestampMatch) {
      const timestamp = parseInt(timestampMatch[1], 10);
      try {
        const date = new Date(timestamp);
        if (date.toString() !== 'Invalid Date') {
          return date.toLocaleString(undefined, {
            month: 'long',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            second: '2-digit',
            hour12: true
          });
        }
      } catch (error) {
        // console.error('Error formatting timestamp:', error);
      }
    }
  }
  
  // Fallback to file name without extension for non-SuprArt files
  if (!file.name.includes('SuprArtData-')) {
    return file.name.replace(/\.\w+$/, '');
  }
  
  // Fallback to 'Untitled'
  return 'Untitled';
};

// ============= Style Constants =============
const toolbarStyles = {
  minHeight: { xs: 40, sm: 40 }, // Match bottom bar height
  px: { xs: 1, sm: 2 },
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on mobile
  alignItems: 'center',
  gap: { xs: 1, sm: 1.5 },
  backdropFilter: 'blur(16px)',
  backgroundColor: 'background.paper', // Match bottom bar color
  borderBottom: 1,
  borderColor: 'rgba(255, 255, 255, 0.05)',
  boxShadow: '0px 2px 12px rgba(0, 0, 0, 0.2)',
  transition: 'all 0.3s ease',
  position: 'relative',
  zIndex: 10,
  py: 0, // Remove vertical padding for compact height
};

const toggleButtonStyles = {
  common: {
    px: { xs: 1.5, sm: 2 },
    py: 0.25,
    minWidth: { xs: '55px', sm: '85px' },
    height: '28px',
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
    fontWeight: 'bold',
    borderRadius: 1.5,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    border: '1px solid transparent',
  },
  live: {
    px: { xs: 1.5, sm: 2 },
    py: 0.25,
    minWidth: { xs: '55px', sm: '85px' },
    height: '28px',
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
    fontWeight: 'bold',
    borderRadius: 1.5,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#22d3ee', // Neon Cyan
    backgroundColor: 'rgba(34, 211, 238, 0.05)',
    border: '1px solid rgba(34, 211, 238, 0.2)',
    '&.Mui-selected': {
      backgroundColor: 'rgba(34, 211, 238, 0.15)',
      color: '#22d3ee',
      borderColor: '#22d3ee',
      boxShadow: '0 0 20px rgba(34, 211, 238, 0.3), inset 0 0 15px rgba(34, 211, 238, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(34, 211, 238, 0.2)',
      }
    },
    '&:hover': {
      backgroundColor: 'rgba(34, 211, 238, 0.1)',
      borderColor: 'rgba(34, 211, 238, 0.4)',
      transform: 'translateY(-2px)',
      boxShadow: '0 0 15px rgba(34, 211, 238, 0.2)',
    }
  },
  artwork: {
    px: { xs: 1.5, sm: 2 },
    py: 0.25,
    minWidth: { xs: '55px', sm: '85px' },
    height: '28px',
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
    fontWeight: 'bold',
    borderRadius: 1.5,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#e879f9', // Neon Magenta
    backgroundColor: 'rgba(232, 121, 249, 0.05)',
    border: '1px solid rgba(232, 121, 249, 0.2)',
    '&.Mui-selected': {
      backgroundColor: 'rgba(232, 121, 249, 0.15)',
      color: '#e879f9',
      borderColor: '#e879f9',
      boxShadow: '0 0 20px rgba(232, 121, 249, 0.3), inset 0 0 15px rgba(232, 121, 249, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(232, 121, 249, 0.2)',
      }
    },
    '&:hover': {
      backgroundColor: 'rgba(232, 121, 249, 0.1)',
      borderColor: 'rgba(232, 121, 249, 0.4)',
      transform: 'translateY(-2px)',
      boxShadow: '0 0 15px rgba(232, 121, 249, 0.2)',
    }
  },
  video: {
    px: { xs: 1.5, sm: 2 },
    py: 0.25,
    minWidth: { xs: '55px', sm: '85px' },
    height: '28px',
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
    fontWeight: 'bold',
    borderRadius: 1.5,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#34d399', // Neon Green
    backgroundColor: 'rgba(52, 211, 153, 0.05)',
    border: '1px solid rgba(52, 211, 153, 0.2)',
    '&.Mui-selected': {
      backgroundColor: 'rgba(52, 211, 153, 0.15)',
      color: '#34d399',
      borderColor: '#34d399',
      boxShadow: '0 0 20px rgba(52, 211, 153, 0.3), inset 0 0 15px rgba(52, 211, 153, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(52, 211, 153, 0.2)',
      }
    },
    '&:hover': {
      backgroundColor: 'rgba(52, 211, 153, 0.1)',
      borderColor: 'rgba(52, 211, 153, 0.4)',
      transform: 'translateY(-2px)',
      boxShadow: '0 0 15px rgba(52, 211, 153, 0.2)',
    }
  },
  profile: {
    px: { xs: 1.5, sm: 2 },
    py: 0.25,
    minWidth: { xs: '55px', sm: '85px' },
    height: '28px',
    fontSize: { xs: '0.7rem', sm: '0.8rem' },
    fontWeight: 'bold',
    borderRadius: 1.5,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    color: '#facc15', // Neon Yellow
    backgroundColor: 'rgba(250, 204, 21, 0.05)',
    border: '1px solid rgba(250, 204, 21, 0.2)',
    '&.Mui-selected': {
      backgroundColor: 'rgba(250, 204, 21, 0.15)',
      color: '#facc15',
      borderColor: '#facc15',
      boxShadow: '0 0 20px rgba(250, 204, 21, 0.3), inset 0 0 15px rgba(250, 204, 21, 0.1)',
      '&:hover': {
        backgroundColor: 'rgba(250, 204, 21, 0.2)',
      }
    },
    '&:hover': {
      backgroundColor: 'rgba(250, 204, 21, 0.1)',
      borderColor: 'rgba(250, 204, 21, 0.4)',
      transform: 'translateY(-2px)',
      boxShadow: '0 0 15px rgba(250, 204, 21, 0.2)',
    }
  }
};


// Update the useStyles constant with these improved mobile-specific styles
const useStyles = {
  sidebar: {
    width: { xs: '100%', sm: '30%', md: '25%', lg: '20%' },
    p: { xs: 1.5, sm: 2 },
    borderRight: { xs: 0, sm: 1 },
    borderBottom: { xs: 1, sm: 0 },
    borderColor: 'rgba(255, 255, 255, 0.05)',
    display: 'flex',
    flexDirection: 'column', // Always vertical for better mobile experience
    gap: { xs: 1, sm: 2 },
    overflowY: 'auto',
    maxHeight: { xs: '45vh', sm: 'none' }, // Limit height on mobile
    backgroundColor: 'background.paper',
    boxShadow: { xs: '0px 2px 4px rgba(0,0,0,0.1)', sm: 'none' },
    zIndex: 5,
    minHeight: { xs: '250px', sm: 'auto' } // Ensure minimum height on mobile
  },
  sidebarButton: {
    width: '100%',
    fontSize: { xs: '0.8rem', sm: '0.85rem' },
    fontWeight: 500,
    py: { xs: 1, sm: 1.5 },
    px: 2,
    height: { xs: 42, sm: 46 },
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    borderRadius: '8px',
    border: '1px solid transparent',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    position: 'relative',
    '&:hover': {
      transform: 'translateY(-2px)',
    }
  },
  fileGrid: {
    flex: 1, // Take remaining space
    marginRight: { xs: 0, sm: 0, lg: '40%' }, // Reserve space for preview on large screens (40% width)
    height: '100%', // Full height to show all files
    overflow: 'auto',
    backgroundColor: 'background.default',
    p: { xs: 1, sm: 2 },
    transition: 'all 0.3s ease',
    '&::-webkit-scrollbar': {
      width: '8px',
      background: 'transparent'
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#40444B',
      borderRadius: '4px',
      '&:hover': {
        background: '#96989D'
      }
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent'
    }
  },
  previewPanel: {
    width: { xs: '100%', md: '30%', lg: '30%' },
    p: { xs: 1.5, sm: 2 },
    borderLeft: { xs: 0, md: 1 },
    borderTop: { xs: 1, md: 0 },
    borderColor: 'rgba(255, 255, 255, 0.05)',
    display: { xs: 'none', md: 'flex' }, // Hide on mobile, show on medium screens and up
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: { xs: 'auto', md: '100%' },
    minHeight: { xs: '300px', md: '400px' },
    backgroundColor: 'background.paper',
    boxShadow: { xs: '0px -2px 4px rgba(0,0,0,0.1)', md: 'none' },
    transition: 'all 0.3s ease',
  },
  mainContent: {
    flex: 1,
    display: 'flex',
    overflow: 'hidden',
    flexDirection: { xs: 'column', sm: 'row' }, // Column on mobile, row on desktop
    backgroundColor: 'background.default',
    borderRadius: 0, // Square corners for clean edges
    height: { xs: 'auto', sm: '100%' }, // Full height on larger screens
    position: 'relative', // For absolute positioned preview panel
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    px: 2,
    py: 1.25,
    borderBottom: 1,
    borderColor: 'rgba(255, 255, 255, 0.05)',
    backgroundColor: 'background.paper',
    backdropFilter: 'blur(8px)',
    borderRadius: 0,
    mb: 1,
    overflowX: 'auto',
    whiteSpace: 'nowrap',
    '&::-webkit-scrollbar': {
      height: '4px'
    }
  },
  pathItem: {
    display: 'inline-flex',
    alignItems: 'center',
    px: 1,
    py: 0.5,
    borderRadius: 1,
    fontSize: { xs: '0.8rem', sm: '0.85rem' },
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    }
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    p: 3,
    textAlign: 'center',
    color: 'text.secondary',
    '& svg': {
      fontSize: '3.5rem',
      mb: 2,
      color: 'primary.main',
      opacity: 0.6
    }
  }
};

// ============= Main Component =============
const Dashboard = ({ user }) => {
  // ===== Breakpoint Detection (prevents dual-mounting preview components) =====
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'));

  // Artists stream this screen while creating; show the public username, never the email
  const ownerLabel = user.username || user.display_name;

  // ===== State Management =====
  const [files, setFiles] = useState([]);
  const [folders, setFolders] = useState([]);
  const [currentPath, setCurrentPath] = useState('/');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [previewFile, setPreviewFile] = useState(null);
  const [previewBlob, setPreviewBlob] = useState(null); // Cache preview blob for instant download
  const [sortConfig, setSortConfig] = useState({ field: 'name', direction: 'desc' });
  const [bucketType, setBucketType] = useState('live');
  const [lastSelectedItem, setLastSelectedItem] = useState(null);
  const [generatedVideo, setGeneratedVideo] = useState(null);
  const [bucketSwitching, setBucketSwitching] = useState(false);
  const [pendingVideoSelection, setPendingVideoSelection] = useState(null); // Track video to select after navigation
  
  // Archive feature state
  const [showArchived, setShowArchived] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [folderToArchive, setFolderToArchive] = useState(null);
  const [archiveLoading, setArchiveLoading] = useState(false);

  // ===== Canvas Grouping State (October 2025) =====
  // Groups files by document-name metadata for easier organization in Live bucket
  const [groupingEnabled, setGroupingEnabled] = useState(true);
  const [fileGroups, setFileGroups] = useState({}); // { documentName: [files...] }
  const [groupColors, setGroupColors] = useState({}); // { documentName: colorHex }
  const [selectedGroup, setSelectedGroup] = useState('all'); // For filter dropdown

  // ===== View Mode State (October 2025 - Timeline Feature) =====
  const [viewMode, setViewMode] = useState('timeline'); // 'list' or 'timeline' (default: timeline)

  // ===== Tour Context =====
  const { startTour, tourCompleted, resetTour } = useTour();

  // Handler for starting the guided tour from icon button
  const handleStartTour = () => {
    if (tourCompleted) {
      resetTour();
    }
    startTour(getDashboardTourSteps());
  };

  // Use ref to track current bucket for onClick handlers
  const bucketTypeRef = useRef(bucketType);
  useEffect(() => {
    bucketTypeRef.current = bucketType;
  }, [bucketType]);

  // Reset to list view when switching to buckets that don't support timeline
  useEffect(() => {
    if (bucketType !== 'live' && bucketType !== 'profile') {
      setViewMode('list');
    }
  }, [bucketType]);

  // Refresh contents when showArchived changes
  useEffect(() => {
    if (bucketType === 'artwork') {
      fetchContents(currentPath, 'artwork');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  // ===== Operation State =====
  const [currentOperation, setCurrentOperation] = useState(null);
  const [operationError, setOperationError] = useState(null);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [warningAcknowledged, setWarningAcknowledged] = useState(false);

  // ===== Move Functionality State =====
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveError, setMoveError] = useState(null);
  const [artworkContents, setArtworkContents] = useState({ files: [], folders: [] });

  // ===== Rename Functionality =====
const [renameDialogOpen, setRenameDialogOpen] = useState(false);
const [renameLoading, setRenameLoading] = useState(false);
const [fileToRename, setFileToRename] = useState(null);

  // ===== Delete Confirmation =====
const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
const [itemsToDelete, setItemsToDelete] = useState([]);

  // ===== Timelapse Functionality =====
  const [timelapseDialogOpen, setTimelapseDialogOpen] = useState(false);
  const [timelapseLoading, setTimelapseLoading] = useState(false);
  const [artworkFolders, setArtworkFolders] = useState([]);
  const [selectedTimelapseFolder, setSelectedTimelapseFolder] = useState(null);
  const [timelapseFileCount, setTimelapseFileCount] = useState(0);
  const [timelapseSnapshotCount, setTimelapseSnapshotCount] = useState(0);
  const [timelapseDeltaCount, setTimelapseDeltaCount] = useState(0);
  const [showTimelapseSettings, setShowTimelapseSettings] = useState(false);
  const [timelapseSettings, setTimelapseSettings] = useState({
    fps: 30,
    duration: 10,  // duration in seconds
    frameSelection: 'include-strokes',  // default to include all strokes (complete progression)
    processingMethod: 'browser',  // 'browser' (faster, uses your computer) or 'server' (slower, frees your browser)
    freezeDuration: 3  // seconds to hold on the last frame (0 = no freeze)
});
const [canvasDimensions, setCanvasDimensions] = useState({ width: 0, height: 0 }); // NEW: for smart recommendations
const [timelapseProgress, setTimelapseProgress] = useState({
    stage: null,
    progress: 0,
    currentFile: 0,
    totalFiles: 0
});

// ===== Collapsible Sidebar State (October 2025) =====
const [leftSidebarOpen, setLeftSidebarOpen] = useState(() => {
  const saved = localStorage.getItem('suprdriveLeftSidebarOpen');
  return saved !== null ? JSON.parse(saved) : true;
});

// Persist sidebar state to localStorage
useEffect(() => {
  localStorage.setItem('suprdriveLeftSidebarOpen', JSON.stringify(leftSidebarOpen));
}, [leftSidebarOpen]);

// Handle pending video selection after navigation
useEffect(() => {
  if (pendingVideoSelection && files.length > 0 && !loading) {
    // Find the file that matches the pending selection
    const videoFile = files.find(file => file.path === pendingVideoSelection);
    if (videoFile) {
      setSelectedFiles([videoFile]);
    }
    // Clear the pending selection regardless of whether we found it
    setPendingVideoSelection(null);
  }
}, [files, pendingVideoSelection, loading]);

// ===== Data Fetching =====
const fetchContents = useCallback(async (path = '/', bucket = bucketType) => {
  // Ensure bucket is valid
  const validBuckets = ['live', 'artwork', 'video', 'profile'];
  const safeBucket = validBuckets.includes(bucket) ? bucket : bucketType;

  try {
    // console.log("Fetching contents:", { path, bucket: safeBucket }); // Temporary debug
    setLoading(true);
    const response = await axios.get('https://drive.suprsketch.com/api/fs/list', {
      withCredentials: true, // Send cookies automatically (Unified Auth System)
      params: { path, bucket: safeBucket },
    });
    
    // console.log("Response received:", response.data); // Temporary debug
    
    // Keep the selected files that are still present in the new data
    const newFiles = response.data.files || [];
    let newFolders = response.data.folders || [];
    
    // Mark archive folder for special styling in artwork bucket
    if (safeBucket === 'artwork') {
      newFolders = newFolders.map(folder => {
        // Mark archive folder for special styling
        if (folder.path.endsWith('archive/') || folder.path.endsWith('archive')) {
          return { ...folder, isArchive: true };
        }
        return folder;
      });
    }
    
    // Filter archived folders if in artwork bucket and showArchived is false
    if (safeBucket === 'artwork' && !showArchived) {
      // Hide folders inside archive unless we're currently inside archive
      if (!path.includes('archive/')) {
        newFolders = newFolders.filter(folder => {
          // Check if this is the archive folder or inside archive
          const isArchiveFolder = folder.path.endsWith('archive/') || folder.path.endsWith('archive');
          const isInsideArchive = folder.path.includes('/archive/');
          const shouldHide = isArchiveFolder || isInsideArchive;
          // Hide the archive folder and any folders inside it
          return !shouldHide;
        });
      }
    }
    
    // Determine if we're navigating to a new location based on path
    const isNewLocation = path !== currentPath;
    
    // Always update bucket type to ensure UI stays in sync
    setBucketType(safeBucket);
    
    // Only reset selections if we're changing paths or buckets
    if (isNewLocation) {
      setSelectedFiles([]);
      setPreviewFile(null);
    } else {
      // For refreshes, try to maintain selections
      setSelectedFiles(prevSelected => {
        // Filter out any selected files that no longer exist in the new data
        return prevSelected.filter(selected => {
          return [...newFiles, ...newFolders].some(item => item.path === selected.path);
        });
      });
      
      // Update preview file if needed
      if (previewFile) {
        const stillExists = newFiles.some(file => file.path === previewFile.path);
        if (!stillExists) {
          setPreviewFile(null);
        }
      }
    }
    
    setFiles(newFiles);
    setFolders(newFolders);
    setCurrentPath(path);
  } catch (error) {
    // console.error("Fetch error details:", error.response || error); // Temporary debug
    
    // Check if this is a 404 error (folder doesn't exist)
    if (error.response?.status === 404) {
      // If we're in a folder that no longer exists, go back to root
      if (path !== '' && path !== '/') {
        setSnackbarMessage('Folder no longer exists, returning to root');
        setShowSnackbar(true);
        // Try to fetch root directory
        fetchContents('', safeBucket);
        return;
      }
    }
    
    // For other errors, show more detailed message
    const errorMessage = error.response?.data?.error || error.message || 'Failed to fetch contents';
    setSnackbarMessage(errorMessage);
    setShowSnackbar(true);
    
    // Clear the file/folder lists to show empty state
    setFiles([]);
    setFolders([]);
  } finally {
    setLoading(false);
    setBucketSwitching(false); // Clear bucket switching state
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [getAuthToken(), showArchived]); // Remove bucketType and currentPath from dependencies to prevent recreation

// ===== Canvas Grouping Logic (October 2025) =====
/**
 * Group files by document-name metadata
 * Only applies to Live bucket - helps artists organize files from same canvas
 *
 * @param {Array} filesList - Array of file objects with metadata
 * @returns {Object} Grouped structure { groupName: [files...] }
 */
const groupFilesByDocument = useCallback((filesList) => {
  // Only group in Live bucket when grouping is enabled
  if (!groupingEnabled || bucketType !== 'live') {
    return { 'all': filesList }; // Return ungrouped if disabled or not Live bucket
  }

  const groups = {};
  let ungroupedCount = 0;

  filesList.forEach(file => {
    // Extract document name from metadata
    let documentName = 'Untitled';

    if (file.metadata) {
      // Check for document-name in metadata (both formats)
      if (file.metadata['document-name'] && file.metadata['document-name'] !== 'Untitled' && file.metadata['document-name'].trim() !== '') {
        documentName = file.metadata['document-name'];
      } else if (file.metadata.document_name && file.metadata.document_name !== 'Untitled' && file.metadata.document_name.trim() !== '') {
        documentName = file.metadata.document_name;
      }
    }

    // Track ungrouped files for logging
    if (documentName === 'Untitled') {
      ungroupedCount++;
    }

    // Initialize group if doesn't exist
    if (!groups[documentName]) {
      groups[documentName] = [];
    }

    // Add file to group
    groups[documentName].push(file);
  });

  return groups;
}, [groupingEnabled, bucketType]);

/**
 * Update file groups when files change
 * Automatically recalculates groups and assigns colors
 */
useEffect(() => {
  if (bucketType === 'live' && files.length > 0 && groupingEnabled) {
    // Group files by document name
    const grouped = groupFilesByDocument(files);
    setFileGroups(grouped);

    // Assign colors to groups
    const documentNames = Object.keys(grouped);
    const colors = getGroupColors(documentNames);
    setGroupColors(colors);
  } else {
    // Clear grouping if not in Live bucket or grouping disabled
    setFileGroups({ 'all': files });
    setGroupColors({});
  }
}, [files, bucketType, groupingEnabled, groupFilesByDocument]);

/**
 * Load grouping preference from localStorage on mount
 */
useEffect(() => {
  const saved = localStorage.getItem('groupingEnabled');
  if (saved !== null) {
    const enabled = saved === 'true';
    setGroupingEnabled(enabled);
  }
}, []);

/**
 * Reset selected group when switching buckets
 * This prevents confusion when moving from Live to other buckets
 */
useEffect(() => {
  if (bucketType !== 'live' && selectedGroup !== 'all') {
    setSelectedGroup('all');
  }
}, [bucketType, selectedGroup]);

/**
 * Clear selection when switching between canvas groups
 * Prevents files from one group staying selected when viewing another group
 */
useEffect(() => {
  if (selectedFiles.length > 0) {
    setSelectedFiles([]);
    setPreviewFile(null);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [selectedGroup]);

/**
 * Get filtered files based on selected group (Sidebar Approach - October 2025)
 * Returns all files if 'all' selected, otherwise returns only files from selected group
 *
 * @returns {Array} Filtered array of files
 */
const getFilteredFiles = useCallback(() => {
  // If not in Live bucket or grouping disabled, show all files
  if (!groupingEnabled || bucketType !== 'live' || selectedGroup === 'all') {
    return files;
  }

  // Return files from selected group
  const filteredFiles = fileGroups[selectedGroup] || [];

  return filteredFiles;
}, [groupingEnabled, bucketType, selectedGroup, files, fileGroups]);

  // ===== Operation Service Setup =====
  useEffect(() => {
    // Set up operation service listeners
    const handleProgress = (progress) => {
      setCurrentOperation(progress);
    };

    const handleComplete = async (result) => {
      setCurrentOperation(null);
      if (result.status.failed.length > 0) {
        setOperationError({
          type: result.type,
          failed: result.status.failed,
          completed: result.status.completed
        });
        
        // Show partial success message for archive operations
        if (result.context && result.context.isArchive) {
          const folderName = result.context.destinationPath.split('/').pop();
          setSnackbarMessage(`Partially archived "${folderName}": ${result.status.completed} files moved, ${result.status.failed.length} failed. Original folder preserved.`);
          setShowSnackbar(true);
          // Refresh to show current state
          fetchContents(currentPath, 'artwork');
        }
      } else {
        // Provide more specific success messages based on operation type
        let successMessage = `Successfully processed ${result.status.completed} files`;
        
        switch (result.type) {
          case OperationType.MOVE:
            if (result.context && result.context.isArchive) {
              // Archive operation succeeded - now safe to delete the source folder
              const folderName = result.context.destinationPath.split('/').pop();
              
              // Delete the source folder ONLY after confirming all files were moved
              if (result.context.folderToDelete) {
                try {
                  await axios.delete('https://drive.suprsketch.com/api/fs/folder', {
                    headers: { 
                      Authorization: `Bearer ${getAuthToken()}`,
                      'Content-Type': 'application/json'
                    },
                    data: {
                      path: result.context.folderToDelete.pathWithoutUser,
                      bucket: 'artwork'
                    }
                  });
                  
                  // Remove folder from UI only after successful deletion
                  setFolders(prevFolders => 
                    prevFolders.filter(folder => folder.path !== result.context.folderToDelete.path)
                  );
                  setSelectedFiles([]);
                  
                  successMessage = `Successfully archived folder "${folderName}" with ${result.status.completed} files`;
                } catch (deleteError) {
                  // If folder deletion fails, show warning but operation is still successful
                  successMessage = `Archived "${folderName}" (${result.status.completed} files) but couldn't remove original folder. Please delete it manually.`;
                  // console.error('Failed to delete source folder after archive:', deleteError);
                }
              } else {
                successMessage = `Successfully archived folder "${folderName}" with ${result.status.completed} files`;
              }
              
              // Always show archived folders after archiving and refresh
              setShowArchived(true);
              // Small delay to ensure state update is processed
              setTimeout(() => {
                fetchContents(currentPath, 'artwork');
              }, 100);
              
            } else if (result.context && result.context.isCrossBucketMove) {
              // Cross-bucket move from Live to Artwork
              successMessage = `Successfully moved ${result.status.completed} file${result.status.completed > 1 ? 's' : ''} to "${result.context.destinationName}" in Artwork bucket`;
            } else if (result.context && result.context.destinationName) {
              // Within-bucket move in Artwork
              successMessage = `Successfully moved ${result.status.completed} file${result.status.completed > 1 ? 's' : ''} to "${result.context.destinationName}"`;
            } else {
              // Fallback message
              successMessage = `Successfully moved ${result.status.completed} file${result.status.completed > 1 ? 's' : ''}`;
            }
            break;
          case OperationType.DELETE:
            successMessage = `Successfully deleted ${result.status.completed} file${result.status.completed > 1 ? 's' : ''}`;
            break;
          case OperationType.DOWNLOAD:
            successMessage = `Successfully downloaded ${result.status.completed} file${result.status.completed > 1 ? 's' : ''}`;
            break;
          case OperationType.PROFILE:
            successMessage = `Successfully sent ${result.status.completed} file${result.status.completed > 1 ? 's' : ''} to gallery`;
            break;
          case OperationType.UPLOAD_TO_GALLERY:
            successMessage = `Successfully uploaded ${result.status.completed} file${result.status.completed > 1 ? 's' : ''} to gallery`;
            // Refresh gallery contents using context values (not closure values)
            if (result.context && result.context.bucketType === 'profile') {
              fetchContents(result.context.currentPath || '/', 'profile');
            }
            // Also refresh if we're currently viewing the gallery
            else if (bucketType === 'profile') {
              fetchContents(currentPath, 'profile');
            }
            break;
          default:
            // Keep the generic message for any other operation types
            break;
        }

        setSnackbarMessage(successMessage);
        setShowSnackbar(true);
      }
      // Removed fetchContents call - using optimistic updates instead
    };

    const handleError = (error) => {
      setOperationError({
        type: 'error',
        message: error.message || 'Operation failed'
      });
      setCurrentOperation(null);
    };

    operationService.on('progress', handleProgress);
    operationService.on('complete', handleComplete);
    operationService.on('error', handleError);

    return () => {
      operationService.off('progress', handleProgress);
      operationService.off('complete', handleComplete);
      operationService.off('error', handleError);
    };
}, []);

   
  // ===== Move Functionality Handlers =====
	const fetchArtworkContents = async (path = '/') => {
		try {
		  // When in artwork bucket, fetch current bucket contents for within-bucket moves
		  const targetBucket = bucketType === 'artwork' ? 'artwork' : 'artwork';
		  
		  const response = await axios.get('https://drive.suprsketch.com/api/fs/list', {
			headers: { Authorization: `Bearer ${getAuthToken()}` },
			params: { path, bucket: targetBucket }
		  });
		  setArtworkContents({
			files: response.data.files || [],
			folders: response.data.folders || []
		  });
		} catch (error) {
		  setMoveError('Failed to load directories');
		}
	  };

	  const handleMoveClick = async () => {
		if (bucketType !== 'live' && bucketType !== 'artwork') {
		  setMoveError("Files can only be moved from Live to Artwork or within Artwork folders");
		  return;
		}

		if (selectedFiles.length > OPERATION_LIMITS.MOVE) {
		  setMoveError(`Cannot move more than ${OPERATION_LIMITS.MOVE} files at once. Please select fewer files.`);
		  return;
		}

		// Smart move: No warning needed - backend will preserve active session automatically
		setMoveError(null);
		await fetchArtworkContents();
		setMoveDialogOpen(true);
	  };

	  const executeMove = async (targetPath) => {
    // Declare these variables outside try block for access in catch block
    let movedFolders = [];
    let movedFiles = [];
    // Determine if this is a cross-bucket move (needed in catch block for rollback logic)
    const isCrossBucketMove = bucketType === 'live';

    try {
        setMoveLoading(true);

        const targetBucket = isCrossBucketMove ? 'artwork' : bucketType;

        // Store the files to move for potential rollback
        const filesToMove = [...selectedFiles];

        // For cross-bucket moves (Live→Artwork), DON'T do optimistic update
        // because Smart Move may keep some files in Live (copied, not moved)
        // For within-bucket moves, do optimistic update as normal
        if (!isCrossBucketMove) {
            // Optimistic update - remove files from current view immediately
            movedFolders = filesToMove.filter(item => item.type === 'folder');
            movedFiles = filesToMove.filter(item => item.type === 'file');

            // Remove from UI state immediately
            setFolders(prevFolders =>
                prevFolders.filter(folder =>
                    !movedFolders.some(moved => moved.path === folder.path)
                )
            );
            setFiles(prevFiles =>
                prevFiles.filter(file =>
                    !movedFiles.some(moved => moved.path === file.path)
                )
            );
        }

        // Clear selections
        setSelectedFiles([]);
        setPreviewFile(null);

        // Show immediate feedback
        setSnackbarMessage('Moving files...');
        setShowSnackbar(true);

        const moveHandler = async (filesToMove) => {
            const filesToSend = Array.isArray(filesToMove) ? filesToMove : [filesToMove];

            try {
                // Process files in chunks using the defined limit
                for (let i = 0; i < filesToSend.length; i += OPERATION_LIMITS.CHUNK_SIZE) {
                    const chunk = filesToSend.slice(i, i + OPERATION_LIMITS.CHUNK_SIZE);

                    await axios.post('https://drive.suprsketch.com/api/fs/move/batch', {
                        files: chunk,
                        destinationPath: targetPath,
                        sourceBucket: bucketType,
                        targetBucket
                    }, {
                        headers: {
                            Authorization: `Bearer ${getAuthToken()}`,
                            'Content-Type': 'application/json',
                            'X-Retry-After': '2000'
                        },
                        timeout: 30000
                    });

                    // Small delay between chunks
                    if (i + OPERATION_LIMITS.CHUNK_SIZE < filesToSend.length) {
                        await new Promise(resolve => setTimeout(resolve, 100));
                    }
                }

                // For cross-bucket moves (Live→Artwork), refresh the file list
                // Smart Move may have kept some files in Live (copied, not moved)
                if (isCrossBucketMove) {
                    await fetchContents(currentPath, bucketType);
                }
            } catch (error) {
                // Rollback on error - re-add the moved items (only for within-bucket moves)
                if (!isCrossBucketMove) {
                    setFolders(prevFolders => [...prevFolders, ...movedFolders]);
                    setFiles(prevFiles => [...prevFiles, ...movedFiles]);
                }
                throw error;
            }
        };

        // Find the destination folder name for the success message
        let destinationName = 'folder';
        if (targetPath && targetPath !== '/') {
            const destFolder = artworkContents.folders.find(f => f.path === targetPath);
            destinationName = destFolder ? destFolder.name : targetPath.split('/').pop() || 'folder';
        }
        
        // Safety check - prevent root folder moves
        if (targetPath === '/' && bucketType === 'artwork') {
            setMoveError('Cannot move files to root folder for security reasons');
            setMoveLoading(false);
            return;
        }

        operationService.addOperation({
            type: OperationType.MOVE,
            files: filesToMove,
            handler: moveHandler,
            isBatch: true,
            context: {
                targetBucket: targetBucket,
                sourceBucket: bucketType,
                destinationPath: targetPath,
                destinationName: destinationName,
                isCrossBucketMove: isCrossBucketMove
            }
        });

        setMoveDialogOpen(false);

        // Show success message based on move type
        // Smart move: Active session files are preserved in Live for viewers
        const successMessage = isCrossBucketMove
            ? `Moving ${filesToMove.length} file(s) to Artwork. Active session preserved.`
            : `Moving ${filesToMove.length} file(s) to ${destinationName}`;
        setSnackbarMessage(successMessage);
        setShowSnackbar(true);

    } catch (error) {
        // Rollback on error - re-add the moved items (only for within-bucket moves)
        setMoveError('Failed to move files. Please try again.');
        if (!isCrossBucketMove) {
            setFolders(prevFolders => [...prevFolders, ...movedFolders]);
            setFiles(prevFiles => [...prevFiles, ...movedFiles]);
        }
    } finally {
        setMoveLoading(false);
    }
};

  // ===== Event Handlers =====
const handleBucketChange = (event, newBucket) => {
      // console.log("Bucket change attempt:", { from: bucketType, to: newBucket }); // Add for debugging
  
  if (newBucket !== null) {
          // console.log(`Changing bucket from ${bucketType} to ${newBucket}`);
    
    // Set bucket switching state for loading indicator
    setBucketSwitching(true);
    
    // Navigate to root directory of new bucket
    // fetchContents will handle updating bucketType state
    fetchContents('/', newBucket);
    
    // Clear selections are already handled in fetchContents when isNewLocation is true
  }
};

  const handleSort = (field) => {
    setSortConfig((prevConfig) => ({
      field,
      direction: 
        prevConfig.field === field
          ? prevConfig.direction === 'asc'
            ? 'desc'
            : 'asc'
          : 'asc',
    }));
  };

	const handleCreateFolder = async () => {
	  // Store the folder name before clearing it
	  const tempFolderName = newFolderName;
	  
	  try {
		// Optimistic update - add folder to UI immediately
		const newFolder = {
		  name: tempFolderName,
		  path: bucketType === 'artwork' 
		    ? (currentPath === '/' ? `/${tempFolderName}` : `${currentPath}/${tempFolderName}`)
		    : `/${tempFolderName}`,
		  type: 'folder',
		  size: 0,
		  lastModified: new Date().toISOString()
		};

		// Close dialog and reset name immediately for better UX
		setCreateFolderDialogOpen(false);
		setNewFolderName('');

		// If we're in the artwork bucket, add the folder to the current view
		if (bucketType === 'artwork') {
		  setFolders(prevFolders => [...prevFolders, newFolder]);
		}

		// Show immediate feedback
		setSnackbarMessage('Creating folder...');
		setShowSnackbar(true);

		// Make the API call
		await axios.post(
		  'https://drive.suprsketch.com/api/fs/folder/',
		  { 
			name: tempFolderName, 
			path: bucketType === 'artwork' ? currentPath : '/', 
			bucket: 'artwork'
		  },
		  { headers: { Authorization: `Bearer ${getAuthToken()}` } }
		);
		
		// Update success message
		setSnackbarMessage('Folder created successfully in Artwork bucket');
		setShowSnackbar(true);
		
	  } catch (error) {
		// Rollback on error - remove the optimistically added folder
		if (bucketType === 'artwork') {
		  setFolders(prevFolders => 
		    prevFolders.filter(folder => folder.name !== tempFolderName)
		  );
		}
		
		setSnackbarMessage('Failed to create folder');
		setShowSnackbar(true);
	  }
	};

const handleDeleteClick = () => {
    if (selectedFiles.length === 0) {
        setSnackbarMessage('Please select files to delete');
        setShowSnackbar(true);
        return;
    }

    if (selectedFiles.length > OPERATION_LIMITS.DELETE) {
        setSnackbarMessage(`Cannot delete more than ${OPERATION_LIMITS.DELETE} files at once`);
        setShowSnackbar(true);
        return;
    }

    // Show confirmation dialog
    setItemsToDelete([...selectedFiles]);
    setDeleteConfirmOpen(true);
};

const handleDelete = async () => {
    // Use the items stored when confirmation dialog was opened
    const itemsToDeleteLocal = [...itemsToDelete];
    
    // Optimistic update - remove items from UI immediately
    const deletedFolders = itemsToDeleteLocal.filter(item => item.type === 'folder');
    const deletedFiles = itemsToDeleteLocal.filter(item => item.type === 'file');
    
    // Remove from UI state immediately
    setFolders(prevFolders => 
        prevFolders.filter(folder => 
            !deletedFolders.some(deleted => deleted.path === folder.path)
        )
    );
    setFiles(prevFiles => 
        prevFiles.filter(file => 
            !deletedFiles.some(deleted => deleted.path === file.path)
        )
    );
    
    // Clear selections
    setSelectedFiles([]);
    setPreviewFile(null);
    
    // Show immediate feedback
    setSnackbarMessage('Deleting...');
    setShowSnackbar(true);

    const deleteHandler = async (items) => {        
        const itemsArray = Array.isArray(items) ? items : [items];
        
        try {
            // Separate folders and files
            const folders = itemsArray.filter(item => item.type === 'folder');
            const files = itemsArray.filter(item => item.type === 'file');

            // Process folders first
            for (let i = 0; i < folders.length; i += OPERATION_LIMITS.CHUNK_SIZE) {
                const folderChunk = folders.slice(i, i + OPERATION_LIMITS.CHUNK_SIZE);
                
                for (const folder of folderChunk) {
                    await axios.delete('https://drive.suprsketch.com/api/fs/folder', {
                        headers: { 
                            Authorization: `Bearer ${getAuthToken()}`,
                            'Content-Type': 'application/json'
                        },
                        data: { 
                            path: folder.path,
                            bucket: bucketType 
                        },
                    });
                }

                // Small delay between chunks
                if (i + OPERATION_LIMITS.CHUNK_SIZE < folders.length) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }

            // Then process files in chunks
            for (let i = 0; i < files.length; i += OPERATION_LIMITS.CHUNK_SIZE) {
                const fileChunk = files.slice(i, i + OPERATION_LIMITS.CHUNK_SIZE);
                
                await axios.delete('https://drive.suprsketch.com/api/fs/files/batch', {
                    headers: { 
                        Authorization: `Bearer ${getAuthToken()}`,
                        'Content-Type': 'application/json'
                    },
                    data: { 
                        files: fileChunk,
                        bucket: bucketType 
                    },
                });

                // Small delay between chunks
                if (i + OPERATION_LIMITS.CHUNK_SIZE < files.length) {
                    await new Promise(resolve => setTimeout(resolve, 100));
                }
            }
        } catch (error) {
            // Rollback on error - re-add the deleted items
            setFolders(prevFolders => [...prevFolders, ...deletedFolders]);
            setFiles(prevFiles => [...prevFiles, ...deletedFiles]);
            throw error;
        }
    };

    operationService.addOperation({
        type: OperationType.DELETE,
        files: itemsToDeleteLocal,
        handler: deleteHandler,
        isBatch: true
    });
    
    // Clear the stored items
    setItemsToDelete([]);
};

const navigateToVideo = (videoDetails) => {
  // Extract folder path and ensure it ends with /
  const folderPath = videoDetails.key.split('/').slice(0, -1).join('/') + '/';
  // Store the video key to select after navigation completes
  setPendingVideoSelection(videoDetails.key);
  setBucketType('video');
  fetchContents(folderPath, 'video');
};

const handleRenameClick = () => {
  if (selectedFiles.length !== 1) {
    setSnackbarMessage('Please select exactly one file to rename');
    setShowSnackbar(true);
    return;
  }
  
  // Only allow renaming in the profile bucket (gallery)
  if (bucketType !== 'profile') {
    setSnackbarMessage('Files can only be renamed in the Gallery section');
    setShowSnackbar(true);
    return;
  }
  
  setFileToRename(selectedFiles[0]);
  setRenameDialogOpen(true);
};

const handleRename = async (newName) => {
  // Declare variables outside try block for proper scope
  let originalFile;
  let newPath;
  
  try {
    setRenameLoading(true);
    
    // Ensure the file extension is preserved
    let finalName = newName;
    const originalName = fileToRename.name;
    
    // Extract extension from the original filename
    const extensionMatch = originalName.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '';
    
    // If the new name doesn't already have the extension, add it
    if (extension && !newName.endsWith(extension)) {
      finalName = `${newName}${extension}`;
    }
    
    // Store original file data for rollback
    originalFile = { ...fileToRename };
    
    // Calculate new path
    const pathParts = fileToRename.path.split('/');
    pathParts[pathParts.length - 1] = finalName;
    newPath = pathParts.join('/');
    
    // Optimistic update - update file name in UI immediately
    setFiles(prevFiles => 
      prevFiles.map(file => 
        file.path === fileToRename.path 
          ? { ...file, name: finalName, path: newPath }
          : file
      )
    );
    
    // Update selected files if the renamed file is selected
    setSelectedFiles(prevSelected => 
      prevSelected.map(file => 
        file.path === fileToRename.path 
          ? { ...file, name: finalName, path: newPath }
          : file
      )
    );
    
    // Update preview file if it's the renamed file
    if (previewFile && previewFile.path === fileToRename.path) {
      setPreviewFile({ ...previewFile, name: finalName, path: newPath });
    }
    
    // Close dialog immediately for better UX
    setRenameDialogOpen(false);
    
    // Show immediate feedback
    setSnackbarMessage('Renaming file...');
    setShowSnackbar(true);
    
    // Make the API call
    await axios.post('https://drive.suprsketch.com/api/fs/rename', {
      path: fileToRename.path,
      newName: finalName,
      bucket: 'profile'
    }, {
      headers: { 
        Authorization: `Bearer ${getAuthToken()}`,
        'Content-Type': 'application/json'
      }
    });
    
    setSnackbarMessage('File renamed successfully');
    setShowSnackbar(true);
    
  } catch (error) {
    // Rollback on error - restore original file name
    if (originalFile && newPath) {
      setFiles(prevFiles => 
        prevFiles.map(file => 
          file.path === newPath 
            ? originalFile
            : file
        )
      );
      
      // Restore in selected files
      setSelectedFiles(prevSelected => 
        prevSelected.map(file => 
          file.path === newPath 
            ? originalFile
            : file
        )
      );
      
      // Restore preview file if needed
      if (previewFile && previewFile.path === newPath) {
        setPreviewFile(originalFile);
      }
    }
    
    setSnackbarMessage('Failed to rename file. Please try again.');
    setShowSnackbar(true);
  } finally {
    setRenameLoading(false);
  }
};

const handleTimelapseClick = async () => {
    try {
        // Reset all timelapse-related states
        setGeneratedVideo(null);
        setSelectedTimelapseFolder(null);
        setShowTimelapseSettings(false);
        setTimelapseProgress({
            stage: null,
            progress: 0,
            currentFile: 0,
            totalFiles: 0
        });
        
        // Fetch folders from artwork bucket
        const response = await axios.get('https://drive.suprsketch.com/api/fs/list', {
            withCredentials: true, // Send cookies automatically (Unified Auth System)
            params: { path: '/', bucket: 'artwork' }
        });
        
        setArtworkFolders(response.data.folders || []);
        setTimelapseDialogOpen(true);
    } catch (error) {
        setSnackbarMessage("Failed to load folders");
        setShowSnackbar(true);
    }
};

const handleSendToProfile = async () => {
  if (!['live', 'artwork', 'video'].includes(bucketType)) {
    setSnackbarMessage("Files can only be sent to profile from Live, Artwork, or Videos buckets");
    setShowSnackbar(true);
    return;
  }

  if (selectedFiles.length > OPERATION_LIMITS.MOVE) {
    setSnackbarMessage(`Cannot send more than ${OPERATION_LIMITS.MOVE} files at once. Please select fewer files.`);
    setShowSnackbar(true);
    return;
  }

  try {
    setMoveLoading(true);

    const sendToProfileHandler = async (filesToSend) => {
      const filesToProcess = Array.isArray(filesToSend) ? filesToSend : [filesToSend];
      
      // Process files in chunks using the defined limit
      for (let i = 0; i < filesToProcess.length; i += OPERATION_LIMITS.CHUNK_SIZE) {
        const chunk = filesToProcess.slice(i, i + OPERATION_LIMITS.CHUNK_SIZE);
        
        await axios.post('https://drive.suprsketch.com/api/fs/send-to-profile', {
          files: chunk,
          sourceBucket: bucketType // Use current bucket type
        }, {
          headers: { 
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          timeout: 30000
        });

        // Small delay between chunks
        if (i + OPERATION_LIMITS.CHUNK_SIZE < filesToProcess.length) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    };

    operationService.addOperation({
      type: 'PROFILE',
      files: selectedFiles,
      handler: sendToProfileHandler
    });

    setSnackbarMessage("Sending to gallery...");
    setShowSnackbar(true);

  } catch (error) {
    setSnackbarMessage('Failed to send files to gallery. Please try again.');
    setShowSnackbar(true);
  } finally {
    setMoveLoading(false);
  }
};

const handleUploadToGallery = () => {
  // Create a hidden file input element
  const fileInput = document.createElement('input');
  fileInput.type = 'file';
  fileInput.multiple = true;
  // Allow only image and video files
  fileInput.accept = 'image/jpeg,image/png,image/gif,image/webp,video/mp4';
  
  // Handle file selection
  fileInput.onchange = async (event) => {
    if (!event.target.files.length) return;
    
    try {
      // Convert FileList to Array for easier handling
      const files = Array.from(event.target.files);
      
      // Prepare file objects in the format the operation service expects
      const fileObjects = files.map(file => ({
        name: file.name,
        path: file.name, // We need a path property for the operation service
        size: file.size,
        type: file.type,
        originalFile: file // Keep the original file object
      }));
      
      // Create a handler for the upload operation
      const uploadToGalleryHandler = async (filesToUpload, context, progressCallback) => {
        // Ensure we're working with an array
        const filesArray = Array.isArray(filesToUpload) ? filesToUpload : [filesToUpload];

        // Calculate total bytes to upload
        const totalBytes = filesArray.reduce((sum, file) => sum + file.size, 0);
        let uploadedBytes = 0;

        // Process files one by one
        for (let i = 0; i < filesArray.length; i++) {
          const fileObj = filesArray[i];
          const formData = new FormData();
          formData.append('file', fileObj.originalFile);

          await axios.post('https://drive.suprsketch.com/api/fs/upload-to-gallery', formData, {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              'Content-Type': 'multipart/form-data'
            },
            timeout: 120000, // Increase from 30000 to 120000 (2 minutes)
            onUploadProgress: (progressEvent) => {
              // Calculate progress for this file
              const fileProgress = progressEvent.loaded;
              const previousFilesBytes = filesArray.slice(0, i).reduce((sum, f) => sum + f.size, 0);
              const currentTotalUploaded = previousFilesBytes + fileProgress;

              // Update operation progress
              const overallProgress = {
                completed: i, // Files completed
                total: filesArray.length,
                uploadProgress: Math.round((currentTotalUploaded / totalBytes) * 100),
                currentFile: i + 1,
                currentFileName: fileObj.name
              };

              // Update via callback if provided
              if (progressCallback) {
                progressCallback(overallProgress);
              }
            }
          });

          uploadedBytes += fileObj.size;
        }
      };
      
      // Add operation to the queue
      operationService.addOperation({
        type: OperationType.UPLOAD_TO_GALLERY, // Use constant instead of string
        files: fileObjects,
        handler: uploadToGalleryHandler,
        context: {
          bucketType: 'profile',
          currentPath: '/' // Gallery uploads always go to root
        }
      });
      
      setSnackbarMessage(`Uploading ${files.length} file${files.length > 1 ? 's' : ''} to gallery...`);
      setShowSnackbar(true);
      
    } catch (error) {
      // console.error('Upload error:', error);
      setSnackbarMessage('Failed to upload files to gallery. Please try again.');
      setShowSnackbar(true);
    }
  };
  
  // Trigger the file selection dialog
  fileInput.click();
};

// Helper function to estimate processing time based on settings
const getEstimatedProcessingTime = () => {
    const frames = timelapseFileCount;
    const { quality, resolution } = timelapseSettings;

    // Base time: ~3 seconds per frame for 4K (conservative estimate)
    let baseTime = frames * 3;

    // Adjust for quality preset
    const qualityMultipliers = {
        high: 1.15,   // 15% slower
        medium: 1.0,  // baseline
        fast: 0.8     // 20% faster
    };
    baseTime *= qualityMultipliers[quality] || 1.0;

    // Adjust for resolution
    const resolutionMultipliers = {
        original: 1.0,  // baseline
        '1080p': 0.5,   // 50% faster
        '720p': 0.35    // 65% faster
    };
    baseTime *= resolutionMultipliers[resolution] || 1.0;

    // Convert to human-readable format
    const minutes = Math.ceil(baseTime / 60);
    if (minutes < 1) return 'Less than 1 minute';
    if (minutes === 1) return '~1 minute';
    if (minutes < 5) return `~${minutes} minutes`;

    // Show range for longer times
    const minTime = Math.max(1, minutes - 2);
    const maxTime = minutes + 2;
    return `~${minTime}-${maxTime} minutes`;
};

const handleGenerateTimelapse = async (folderPath) => {
    try {
        setTimelapseLoading(true);

        const response = await axios.post(
            'https://drive.suprsketch.com/api/fs/timelapse',
            {
                folderPath,
                bucket: 'artwork',
                options: {
                    fps: timelapseSettings.fps,
                    quality: 'fast',  // Fixed: always use fast for better performance
                    resolution: 'original',  // Fixed: always use original resolution
                    codec: 'libx264',
                    frameSelection: timelapseSettings.frameSelection
                }
            },
            {
                withCredentials: true, // Send cookies for authentication (Unified Auth System)
                headers: {
                    'Content-Type': 'application/json'
                },
                timeout: 3600000, // 1 hour timeout
                maxContentLength: Infinity,
                maxBodyLength: Infinity
            }
        );

        // Handle successful response (now contains video details instead of blob)
        if (response.data.success) {
          setGeneratedVideo(response.data.video);
          setSnackbarMessage("Timelapse generated successfully!");
          setShowSnackbar(true);
          // Don't close the dialog here anymore
          setSelectedTimelapseFolder(null);
          setShowTimelapseSettings(false);
      }

  } catch (error) {
      setSnackbarMessage("Failed to generate timelapse. Please try again.");
      setShowSnackbar(true);
      setTimelapseDialogOpen(false);
  } finally {
      setTimelapseLoading(false);
  }
};

// Add download handler
  const handleDownload = async () => {
    if (selectedFiles.length > OPERATION_LIMITS.DOWNLOAD) {
      setSnackbarMessage(`Cannot download more than ${OPERATION_LIMITS.DOWNLOAD} files at once`);
      setShowSnackbar(true);
      return;
    }

    try {
      if (selectedFiles.length === 1) {
        const file = selectedFiles[0];
        let blob;

        // Check if we have a cached preview blob for this exact file
        if (previewBlob && previewFile && previewFile.path === file.path) {
          // Use cached blob - instant download!
          blob = previewBlob;
        } else {
          // Fetch from server (file not previewed yet)
          const response = await axios.get(
            `https://drive.suprsketch.com/api/fs/download/${encodeURIComponent(file.path)}`,
            {
              withCredentials: true, // Send cookies automatically (Unified Auth System)
              params: { bucket: bucketType },
              responseType: 'blob'
            }
          );
          blob = response.data;
        }

        const url = window.URL.createObjectURL(new Blob([blob]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', (file.name.endsWith('.json') || file.name.endsWith('.suprs') || file.name.endsWith('.supr')) ? file.name.replace(/\.(json|suprs|supr)$/, '.png') : file.name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
	  } else if (selectedFiles.length > 1) {
    const downloadHandler = async () => {
        const response = await axios.post(
            'https://drive.suprsketch.com/api/fs/download-batch',
            { items: selectedFiles, bucket: bucketType },
            {
                withCredentials: true, // Send cookies automatically (Unified Auth System)
                responseType: 'blob'
            }
        );
        
        // Create and trigger download like we do for single files
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', 'SuprArtFiles.zip');  // Name for the zip file
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        return response.data;
    };

    operationService.addOperation({
        type: OperationType.DOWNLOAD,
        files: [{ name: 'batch' }],
        handler: downloadHandler
    });
}
    } catch (error) {
	  setSnackbarMessage('Download failed');
      setShowSnackbar(true);
    }
  };

// ===== Archive Functionality =====
const handleArchiveClick = () => {
  // Only allow archiving single folders
  const selectedFolders = selectedFiles.filter(item => item.type === 'folder');
  
  if (selectedFolders.length === 0) {
    setSnackbarMessage('Please select a folder to archive');
    setShowSnackbar(true);
    return;
  }
  
  if (selectedFolders.length > 1) {
    setSnackbarMessage('Please select only one folder to archive at a time');
    setShowSnackbar(true);
    return;
  }
  
  const folder = selectedFolders[0];
  
  // Don't allow archiving folders that are already in archive
  if (folder.path.startsWith('archive/')) {
    setSnackbarMessage('This folder is already archived');
    setShowSnackbar(true);
    return;
  }
  
  setFolderToArchive(folder);
  setArchiveDialogOpen(true);
};

const handleUnarchiveClick = () => {
  // Only allow unarchiving single folders
  const selectedFolders = selectedFiles.filter(item => item.type === 'folder');
  
  if (selectedFolders.length === 0) {
    setSnackbarMessage('Please select an archived folder to restore');
    setShowSnackbar(true);
    return;
  }
  
  if (selectedFolders.length > 1) {
    setSnackbarMessage('Please select only one folder to restore at a time');
    setShowSnackbar(true);
    return;
  }
  
  const folder = selectedFolders[0];
  
  // Only allow unarchiving folders that are in archive
  if (!folder.path.startsWith('archive/')) {
    setSnackbarMessage('This folder is not archived');
    setShowSnackbar(true);
    return;
  }
  
  // Extract the original folder name from the archive path
  const folderName = folder.name;
  executeUnarchive(folder, folderName);
};

// Helper function to recursively fetch all contents of a folder
const fetchFolderContentsRecursively = async (folderPath, bucket) => {
  const allFiles = [];
  const foldersToProcess = [folderPath];
  const processedFolders = new Set(); // Prevent infinite loops
  
  while (foldersToProcess.length > 0) {
    const currentPath = foldersToProcess.shift();
    
    // Skip if we've already processed this folder
    if (processedFolders.has(currentPath)) {
      continue;
    }
    processedFolders.add(currentPath);
    
    try {
      const response = await axios.get('https://drive.suprsketch.com/api/fs/list', {
        withCredentials: true, // Send cookies automatically (Unified Auth System)
        params: { 
          path: currentPath,
          bucket: bucket 
        }
      });
      
      const files = response.data.files || [];
      const folders = response.data.folders || [];
      
      allFiles.push(...files);
      
      // Add subfolders to process queue
      folders.forEach(folder => {
        if (!processedFolders.has(folder.path)) {
          foldersToProcess.push(folder.path);
        }
      });
    } catch (error) {
      // If a folder doesn't exist or we can't access it, skip it
      // console.error('Error fetching folder contents:', error);
      // Continue processing other folders
      continue;
    }
  }
  return allFiles;
};

const executeArchive = async () => {
  try {
    setArchiveLoading(true);
    
    const folderPath = folderToArchive.path;
    const folderName = folderToArchive.name;
    
    // First, check if archive folder exists
    try {
      await axios.get('https://drive.suprsketch.com/api/fs/list', {
        withCredentials: true, // Send cookies automatically (Unified Auth System)
        params: { 
          path: 'archive',
          bucket: 'artwork' 
        }
      });
    } catch (error) {
      // Archive folder doesn't exist, create it
      if (error.response?.status === 404) {
        await axios.post('https://drive.suprsketch.com/api/fs/folder', {
          name: 'archive',
          path: '',
          bucket: 'artwork'
        }, {
          headers: { 
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          }
        });
      }
    }
    
    // Fetch all files in the folder to move
    const allFiles = await fetchFolderContentsRecursively(folderPath, 'artwork');
    
    if (allFiles.length === 0) {
      setArchiveDialogOpen(false);
      setSnackbarMessage(`Folder "${folderName}" is empty.`);
      setShowSnackbar(true);
      setArchiveLoading(false);
      return;
    }
    
    // DO NOT optimistically remove folder - wait for confirmation
    // Show immediate feedback
    setSnackbarMessage('Archiving folder...');
    setShowSnackbar(true);
    
    // Create move handler using the same approach as executeMove
    const archiveHandler = async (filesToArchive) => {
      const filesToSend = Array.isArray(filesToArchive) ? filesToArchive : [filesToArchive];
      
      try {
        // Process files in chunks using the defined limit
        for (let i = 0; i < filesToSend.length; i += OPERATION_LIMITS.CHUNK_SIZE) {
          const chunk = filesToSend.slice(i, i + OPERATION_LIMITS.CHUNK_SIZE);
          
          // Format files for batch move endpoint - strip user prefix since backend adds it
          const formattedChunk = chunk.map(file => ({
            path: file.path.replace(/^users\/[^\/]+\//, ''),
            name: file.name,
            type: file.type
          }));
          
          // The backend moveFiles doesn't prepend user path to destination, so we need to include it
          const userPrefix = `users/${user.id}/`;
          
          await axios.post('https://drive.suprsketch.com/api/fs/move/batch', {
            files: formattedChunk,
            sourcePath: '',  // Not needed when files have full paths
            destinationPath: `${userPrefix}archive/${folderName}`,
            sourceBucket: 'artwork',
            targetBucket: 'artwork'
          }, {
            headers: { 
              Authorization: `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json',
              'X-Retry-After': '2000'
            },
            timeout: 30000
          });

          // Small delay between chunks
          if (i + OPERATION_LIMITS.CHUNK_SIZE < filesToSend.length) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
        
        // DO NOT delete folder here - let operationService handle completion
      } catch (error) {
        // Rollback on error - refresh to show current state
        fetchContents(currentPath, 'artwork');
        throw error;
      }
    };
    
    // Use operationService to handle the archive operation
    operationService.addOperation({
      type: OperationType.MOVE,
      files: allFiles,
      handler: archiveHandler,
      isBatch: true,
      context: {
        targetBucket: 'artwork',
        sourceBucket: 'artwork',
        destinationPath: `archive/${folderName}`,
        destinationName: 'archive',
        isArchive: true,
        folderToDelete: {
          path: folderPath,
          name: folderName,
          pathWithoutUser: folderPath.replace(/^users\/[^\/]+\//, '')
        }
      }
    });
    
    setArchiveDialogOpen(false);
    setFolderToArchive(null);
    
  } catch (error) {
    // Handle any other errors
    fetchContents(currentPath, 'artwork');
    setSnackbarMessage('Failed to archive folder. Please try again.');
    setShowSnackbar(true);
  } finally {
    setArchiveLoading(false);
  }
};

const executeUnarchive = async (folder, folderName) => {
  try {
    setArchiveLoading(true);
    
    // Fetch all files in the archived folder recursively
    const allFiles = await fetchFolderContentsRecursively(folder.path, 'artwork');
    
    if (allFiles.length === 0) {
      // Just remove the empty archive folder
      setFolders(prevFolders => 
        prevFolders.filter(f => f.path !== folder.path)
      );
      setSnackbarMessage(`Empty folder "${folderName}" removed from archive.`);
      setShowSnackbar(true);
      fetchContents(currentPath, 'artwork');
      setArchiveLoading(false);
      return;
    }
    
    // Optimistically update UI
    setFolders(prevFolders => 
      prevFolders.filter(f => f.path !== folder.path)
    );
    setSelectedFiles([]);
    
    // Use batch move for efficiency
    const batchSize = 100;
    const totalBatches = Math.ceil(allFiles.length / batchSize);
    let successCount = 0;
    
    for (let i = 0; i < totalBatches; i++) {
      const batchFiles = allFiles.slice(i * batchSize, (i + 1) * batchSize);
      
      try {
        // Move files in parallel batches using individual move endpoint
        const movePromises = batchFiles.map(file => {
          // Remove the "archive/foldername/" prefix to restore to original location
          const archivePrefix = `archive/${folderName}/`;
          const targetPath = file.path.startsWith(archivePrefix) 
            ? file.path.substring(archivePrefix.length)
            : file.path.replace(/^archive\//, '');
          
          return axios.post('https://drive.suprsketch.com/api/fs/move', {
            sourcePath: file.path,
            targetPath: targetPath,
            sourceBucket: 'artwork',
            targetBucket: 'artwork'
          }, {
            headers: { 
              Authorization: `Bearer ${getAuthToken()}`,
              'Content-Type': 'application/json'
            }
          });
        });
        
        // Execute moves in parallel for this batch
        await Promise.all(movePromises);
        
        successCount += batchFiles.length;
        
        // Update progress
        const progress = Math.round((successCount / allFiles.length) * 100);
        setSnackbarMessage(`Restoring... ${progress}% (${successCount}/${allFiles.length} files)`);
        setShowSnackbar(true);
        
      } catch (batchError) {
        // If batch fails, show error and stop
        // console.error('Batch restore error:', batchError);
        setSnackbarMessage(`Restore partially failed at ${successCount} files. Please try again.`);
        setShowSnackbar(true);
        break;
      }
    }
    
    if (successCount === allFiles.length) {
      // Delete the archive folder after all files have been restored
      try {
        const folderPathWithoutUser = folder.path.replace(/^users\/[^\/]+\//, '');
        
        await axios.delete('https://drive.suprsketch.com/api/fs/folder', {
          headers: { 
            Authorization: `Bearer ${getAuthToken()}`,
            'Content-Type': 'application/json'
          },
          data: {
            path: folderPathWithoutUser,
            bucket: 'artwork'
          }
        });
        
        // console.log('Successfully deleted archive folder after restoring');
      } catch (deleteError) {
        // Log error but don't fail the operation since files were restored successfully
        // console.error('Note: Archive folder cleanup failed, but files were restored successfully:', deleteError);
      }
      
      setSnackbarMessage(`Folder "${folderName}" with ${allFiles.length} files restored successfully`);
    } else {
      setSnackbarMessage(`Partially restored "${folderName}": ${successCount}/${allFiles.length} files moved.`);
    }
    setShowSnackbar(true);
    
    // Refresh the folder list
    fetchContents(currentPath, 'artwork');
    
  } catch (error) {
    // Rollback on error
    fetchContents(currentPath, 'artwork');
    setSnackbarMessage('Failed to restore folder. Please try again.');
    setShowSnackbar(true);
  } finally {
    setArchiveLoading(false);
  }
};

// ===== Effects =====
const initialMountRef = useRef(true);
const resizeTimeoutRef = useRef(null);
  
useEffect(() => {
  if (initialMountRef.current) {
    fetchContents('/', bucketType);
    initialMountRef.current = false;
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [fetchContents]); // Remove bucketType to prevent re-running on bucket change
  
useEffect(() => {
  if (selectedFiles.length === 0) {
    setWarningAcknowledged(false);
  }
}, [selectedFiles]);

// Handle window resize and orientation changes
useEffect(() => {
  const handleResize = () => {
    // Clear timeout if it exists
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }

    // Set a timeout to avoid excessive re-renders during resizing
    resizeTimeoutRef.current = setTimeout(() => {
      // Force re-render key elements for mobile layout
      const fileGrid = document.querySelector('.file-grid');
      if (fileGrid) {
        fileGrid.style.height = `calc(100vh - ${window.innerWidth < 600 ? 240 : 0}px)`;
        fileGrid.style.height = ''; // Reset to let CSS take over again
      }

      // Adjust bucket selector wrapping if needed
      const bucketSelector = document.querySelector('.bucket-selector');
      if (bucketSelector) {
        if (window.innerWidth < 400) {
          bucketSelector.style.flexWrap = 'wrap';
        } else if (window.innerWidth >= 600) {
          bucketSelector.style.flexWrap = 'nowrap';
        }
      }
    }, 250);
  };

  // Listen for resize and orientation change events
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);

  // Call once on mount to ensure proper initial layout
  handleResize();

  // Cleanup
  return () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
    if (resizeTimeoutRef.current) {
      clearTimeout(resizeTimeoutRef.current);
    }
  };
}, []);

useEffect(() => {
    let pollInterval;
    
    const pollProgress = async () => {
        if (timelapseLoading && selectedTimelapseFolder) {
            try {
                const response = await axios.get('/api/fs/timelapse/status', {
                    withCredentials: true // Send cookies for authentication (Unified Auth System)
                });
                // console.log('Poll Response:', response.data); // Debug logging
                if (response.data.progress) {
                //  console.log('Progress update:', response.data.progress); // Debug logging
                    setTimelapseProgress(response.data.progress);
                    // Only stop polling if encoding is complete
                    if (response.data.progress.stage === 'encoding' && response.data.progress.progress >= 100) {
                        clearInterval(pollInterval);
                        setTimelapseLoading(false);
                    }
                }
            } catch (error) {
                // console.error('Error polling progress:', error);
            }
        }
    };

    // Start polling if loading and folder selected
    if (timelapseLoading && selectedTimelapseFolder) {
        pollInterval = setInterval(pollProgress, 1000); // Poll every second
        pollProgress(); // Do initial poll immediately
    }

    // Cleanup on unmount or when dependencies change
    return () => {
        if (pollInterval) {
            clearInterval(pollInterval);
        }
    };

}, [timelapseLoading, selectedTimelapseFolder, getAuthToken()]);
  
  // ===== Render ===== 
  return (
    <Box 
      sx={{ 
        height: '100vh', // Full height
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        background: theme => theme.palette.gradients.mainBackground,
      }}
    >
      {/* Remove the fixed top spacer */}
      
      {/* App Bar */}
      <AppBar 
        position="static" 
        color="transparent" 
        elevation={0}
        sx={{ 
          backgroundImage: 'none',
          borderBottom: 1,
          borderColor: 'rgba(255, 255, 255, 0.05)',
        }}
      >
        <Toolbar sx={toolbarStyles}>
          {/* Title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mr: { xs: 0, sm: 2 } }}>
            {/* SuprSketch Logo - Links to main site */}
            <Box
              component="a"
              href="https://www.suprsketch.com"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 0.8,
                  transform: 'scale(1.05)',
                }
              }}
            >
              <img
                src="/suprsketch-icon.ico"
                alt="SuprSketch"
                style={{
                  width: '24px',
                  height: '24px',
                  objectFit: 'contain',
                }}
              />
            </Box>

            <Typography variant="h6" sx={{
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              fontWeight: 'bold',
              fontSize: { xs: '0.9rem', sm: '1rem' }
            }}>
              {ownerLabel ? `${ownerLabel}'s Files` : 'My Files'}
            </Typography>
          </Box>
          
          {/* Center Section with Toggle Buttons */}
          <Box sx={{
            position: { xs: 'static', sm: 'absolute' },
            left: { sm: '50%' },
            transform: { sm: 'translateX(-50%)' },
            width: { xs: '100%', sm: '60%' },
            display: 'flex',
            justifyContent: 'center'
          }}>
            {/* Inner container for relative positioning */}
            <Box sx={{ position: 'relative', width: '100%', display: 'flex', justifyContent: 'center' }}>
              {/* Bucket switching loading overlay */}
              <ToggleButtonGroup
                value={bucketType}
                exclusive
                onChange={handleBucketChange}
                className="bucket-selector"
                sx={{
                  display: 'flex',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' }, // Allow wrapping on mobile
                  justifyContent: 'center',
                  gap: { xs: 0.5, sm: 1 }, // Add small gap between buttons on mobile
                  mb: 0 // Remove bottom margin
                }}
              >
                <ToggleButton 
                  value="live" 
                  sx={toggleButtonStyles.live}
                  className="live-bucket"
                  onMouseDown={(e) => {
                    // If already selected, navigate to root on click
                    if (bucketTypeRef.current === 'live') {
                      e.preventDefault();
                      setBucketSwitching(true);
                      fetchContents('/', 'live');
                    }
                  }}
                >
                  LIVE
                </ToggleButton>
                <ToggleButton 
                  value="artwork" 
                  sx={toggleButtonStyles.artwork}
                  className="artwork-bucket"
                  onMouseDown={(e) => {
                    // If already selected, navigate to root on click
                    if (bucketTypeRef.current === 'artwork') {
                      e.preventDefault();
                      setBucketSwitching(true);
                      fetchContents('/', 'artwork');
                    }
                  }}
                >
                  ARTWORK
                </ToggleButton>
                <ToggleButton 
                  value="video" 
                  sx={toggleButtonStyles.video}
                  className="video-bucket"
                  onMouseDown={(e) => {
                    // If already selected, navigate to root on click
                    if (bucketTypeRef.current === 'video') {
                      e.preventDefault();
                      setBucketSwitching(true);
                      fetchContents('/', 'video');
                    }
                  }}
                >
                  VIDEO
                </ToggleButton>
                {/* Gallery tab removed - no longer managing profile artwork here */}
              </ToggleButtonGroup>
            </Box>
          </Box>
          
          {/* Right section - View Toggle and Show Archived */}
          <Box sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}>
            {/* View Mode Toggle - Grid vs Treemap (October 2025) */}
            {/* Only show in Live and Profile buckets where files are grouped by canvas */}
            {(bucketType === 'live' || bucketType === 'profile') && (
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(e, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                  }
                }}
                size="small"
                sx={{
                  height: 28,
                  '& .MuiToggleButton-root': {
                    px: 1.5,
                    py: 0.5,
                    fontSize: '0.75rem',
                    borderColor: 'rgba(255, 255, 255, 0.12)',
                    color: 'text.secondary',
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      }
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    }
                  }
                }}
              >
                <ToggleButton value="list">
                  <Tooltip title="List View" arrow>
                    <ViewListIcon sx={{ fontSize: 18 }} />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="timeline">
                  <Tooltip title="Timeline View" arrow>
                    <ViewModuleIcon sx={{ fontSize: 18 }} />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}


            {/* Show Archived toggle (only in Artwork bucket) */}
            {bucketType === 'artwork' && (
              <FormControlLabel
                control={
                  <Switch
                    checked={showArchived}
                    onChange={(e) => setShowArchived(e.target.checked)}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Show Archived
                  </Typography>
                }
              />
            )}
          </Box>
          
          {/* Remove tour button from here */}
        </Toolbar>
      </AppBar>

      {/* Operation Progress */}
      {currentOperation && <OperationProgress operation={currentOperation} />}

      {/* Main Content Area */}
      <Box sx={useStyles.mainContent}>
        {/* Left Sidebar */}
        <Box sx={{
          width: leftSidebarOpen ? { xs: '100%', sm: '30%', md: '25%', lg: '20%' } : { xs: 0, sm: '60px' },
          minWidth: leftSidebarOpen ? { xs: '250px', sm: '250px' } : { xs: 0, sm: '60px' },
          p: leftSidebarOpen ? { xs: 1.5, sm: 2 } : 0,
          borderRight: { xs: 0, sm: '1px solid rgba(255, 255, 255, 0.05) !important' },
          display: { xs: leftSidebarOpen ? 'flex' : 'none', sm: 'flex' },
          flexDirection: 'column',
          gap: leftSidebarOpen ? { xs: 1, sm: 2 } : 0,
          overflowY: 'auto',
          backgroundColor: 'background.paper',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          alignItems: leftSidebarOpen ? 'stretch' : 'center',
          justifyContent: leftSidebarOpen ? 'flex-start' : 'flex-start',
          pt: leftSidebarOpen ? 0 : 2,
          maxHeight: { xs: '45vh', sm: 'none' },
          boxShadow: { xs: '0px 2px 4px rgba(0,0,0,0.1)', sm: 'none' },
          zIndex: 5,
          minHeight: { xs: '250px', sm: 'auto' }
        }}>
          {leftSidebarOpen ? (
            <>
              {/* Collapse button and header - Neon theme */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, px: 1 }}>
                <Typography variant="overline" sx={{ color: '#71717a', fontWeight: 600, letterSpacing: 2, fontSize: '0.65rem' }}>
                  File Actions
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setLeftSidebarOpen(false)}
                  sx={{
                    display: { xs: 'none', sm: 'inline-flex' },
                    border: '1px solid rgba(255, 255, 255, 0.08)',
                    borderRadius: '6px',
                    padding: '4px',
                    color: '#71717a',
                    transition: 'all 0.3s',
                    '&:hover': {
                      borderColor: '#22d3ee',
                      color: '#22d3ee',
                      boxShadow: '0 0 10px rgba(34, 211, 238, 0.3)',
                    }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
              </Box>

          {/* Refresh Button - Cyan Neon */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<RefreshIcon sx={{ color: '#22d3ee' }} />}
            onClick={() => fetchContents(currentPath, bucketType)}
            className="refresh-button"
            sx={{
              ...useStyles.sidebarButton,
              background: 'rgba(34, 211, 238, 0.1)',
              borderColor: 'rgba(34, 211, 238, 0.3)',
              color: '#fafafa',
              '&:hover': {
                background: 'rgba(34, 211, 238, 0.15)',
                borderColor: '#22d3ee',
                boxShadow: '0 0 20px rgba(34, 211, 238, 0.3), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: 'rgba(34, 211, 238, 0.05)',
                borderColor: 'rgba(34, 211, 238, 0.1)',
                color: 'rgba(250, 250, 250, 0.25)'
              },
              '& .MuiButton-startIcon': {
                color: '#22d3ee'
              }
            }}
          >
            Refresh
          </Button>

          {/* Create Artwork Folder Button - Magenta Neon */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<FolderIcon sx={{ color: '#e879f9' }} />}
            onClick={() => setCreateFolderDialogOpen(true)}
            className="create-artwork-folder-button"
            sx={{
              ...useStyles.sidebarButton,
              background: 'rgba(232, 121, 249, 0.1)',
              borderColor: 'rgba(232, 121, 249, 0.3)',
              color: '#fafafa',
              '&:hover': {
                background: 'rgba(232, 121, 249, 0.15)',
                borderColor: '#e879f9',
                boxShadow: '0 0 20px rgba(232, 121, 249, 0.3), inset 0 0 20px rgba(232, 121, 249, 0.1)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: 'rgba(232, 121, 249, 0.05)',
                borderColor: 'rgba(232, 121, 249, 0.1)',
                color: 'rgba(250, 250, 250, 0.25)'
              },
              '& .MuiButton-startIcon': {
                color: '#e879f9'
              }
            }}
          >
            Create Artwork Folder
          </Button>
          
          {/* Move Button - Only show in live bucket - Magenta Neon */}
          {bucketType === 'live' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={moveLoading ? <CircularProgress size={20} sx={{ color: '#e879f9' }} /> : <MoveIcon sx={{ color: '#e879f9' }} />}
              onClick={handleMoveClick}
              className="move-to-artwork-button"
              disabled={selectedFiles.length === 0 || moveLoading || currentOperation}
              sx={{
                ...useStyles.sidebarButton,
                background: 'rgba(232, 121, 249, 0.1)',
                borderColor: 'rgba(232, 121, 249, 0.3)',
                color: '#fafafa',
                '&:hover': {
                  background: 'rgba(232, 121, 249, 0.15)',
                  borderColor: '#e879f9',
                  boxShadow: '0 0 20px rgba(232, 121, 249, 0.3), inset 0 0 20px rgba(232, 121, 249, 0.1)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(232, 121, 249, 0.05)',
                  borderColor: 'rgba(232, 121, 249, 0.1)',
                  color: 'rgba(250, 250, 250, 0.25)'
                },
                '& .MuiButton-startIcon': {
                  color: '#e879f9'
                }
              }}
            >
              Move to Artwork
            </Button>
          )}
          
          {/* Move Button - Also show in artwork bucket - Magenta Neon */}
          {bucketType === 'artwork' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={moveLoading ? <CircularProgress size={20} sx={{ color: '#e879f9' }} /> : <MoveIcon sx={{ color: '#e879f9' }} />}
              onClick={handleMoveClick}
              className="move-to-folder-button"
              disabled={selectedFiles.length === 0 || moveLoading || currentOperation}
              sx={{
                ...useStyles.sidebarButton,
                background: 'rgba(232, 121, 249, 0.1)',
                borderColor: 'rgba(232, 121, 249, 0.3)',
                color: '#fafafa',
                '&:hover': {
                  background: 'rgba(232, 121, 249, 0.15)',
                  borderColor: '#e879f9',
                  boxShadow: '0 0 20px rgba(232, 121, 249, 0.3), inset 0 0 20px rgba(232, 121, 249, 0.1)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(232, 121, 249, 0.05)',
                  borderColor: 'rgba(232, 121, 249, 0.1)',
                  color: 'rgba(250, 250, 250, 0.25)'
                },
                '& .MuiButton-startIcon': {
                  color: '#e879f9'
                }
              }}
            >
              Move to Folder
            </Button>
          )}
          
          {/* Archive/Unarchive Button - Show in artwork bucket - Gray/Green Neon */}
          {bucketType === 'artwork' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={archiveLoading ? <CircularProgress size={20} sx={{ color: selectedFiles.some(f => f.path && f.path.startsWith('archive/')) ? '#34d399' : '#71717a' }} /> :
                        (selectedFiles.some(f => f.path && f.path.startsWith('archive/')) ? <UnarchiveIcon sx={{ color: '#34d399' }} /> : <ArchiveIcon sx={{ color: '#71717a' }} />)}
              onClick={selectedFiles.some(f => f.path && f.path.startsWith('archive/')) ? handleUnarchiveClick : handleArchiveClick}
              className="archive-button"
              disabled={selectedFiles.filter(f => f.type === 'folder').length !== 1 || archiveLoading || currentOperation}
              sx={{
                ...useStyles.sidebarButton,
                background: selectedFiles.some(f => f.path && f.path.startsWith('archive/'))
                  ? 'rgba(52, 211, 153, 0.1)'
                  : 'rgba(113, 113, 122, 0.1)',
                borderColor: selectedFiles.some(f => f.path && f.path.startsWith('archive/'))
                  ? 'rgba(52, 211, 153, 0.3)'
                  : 'rgba(113, 113, 122, 0.3)',
                color: '#fafafa',
                '&:hover': {
                  background: selectedFiles.some(f => f.path && f.path.startsWith('archive/'))
                    ? 'rgba(52, 211, 153, 0.15)'
                    : 'rgba(113, 113, 122, 0.15)',
                  borderColor: selectedFiles.some(f => f.path && f.path.startsWith('archive/'))
                    ? '#34d399'
                    : '#71717a',
                  boxShadow: selectedFiles.some(f => f.path && f.path.startsWith('archive/'))
                    ? '0 0 20px rgba(52, 211, 153, 0.3), inset 0 0 20px rgba(52, 211, 153, 0.1)'
                    : '0 0 20px rgba(113, 113, 122, 0.2)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(113, 113, 122, 0.05)',
                  borderColor: 'rgba(113, 113, 122, 0.1)',
                  color: 'rgba(250, 250, 250, 0.25)'
                },
                '& .MuiButton-startIcon': {
                  color: selectedFiles.some(f => f.path && f.path.startsWith('archive/')) ? '#34d399' : '#71717a'
                }
              }}
            >
              {selectedFiles.some(f => f.path && f.path.startsWith('archive/')) ? 'Restore from Archive' : 'Archive Folder'}
            </Button>
          )}
          
          {/* Send to Gallery Button - Yellow Neon */}
          {bucketType !== 'profile' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={moveLoading ? <CircularProgress size={20} sx={{ color: '#facc15' }} /> : <AccountCircleIcon sx={{ color: '#facc15' }} />}
              onClick={handleSendToProfile}
              className="send-to-gallery-button"
              disabled={selectedFiles.length !== 1 || selectedFiles[0]?.type === 'folder' || moveLoading || currentOperation}
              sx={{
                ...useStyles.sidebarButton,
                background: 'rgba(250, 204, 21, 0.1)',
                borderColor: 'rgba(250, 204, 21, 0.3)',
                color: '#fafafa',
                '&:hover': {
                  background: 'rgba(250, 204, 21, 0.15)',
                  borderColor: '#facc15',
                  boxShadow: '0 0 20px rgba(250, 204, 21, 0.3), inset 0 0 20px rgba(250, 204, 21, 0.1)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(250, 204, 21, 0.05)',
                  borderColor: 'rgba(250, 204, 21, 0.1)',
                  color: 'rgba(250, 250, 250, 0.25)'
                },
                '& .MuiButton-startIcon': {
                  color: '#facc15'
                }
              }}
            >
              Send to Gallery
            </Button>
          )}

          {/* Upload to Gallery Button - Cyan Neon */}
          {bucketType === 'profile' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<CloudUploadIcon sx={{ color: '#22d3ee' }} />}
              onClick={handleUploadToGallery}
              disabled={moveLoading || currentOperation}
              sx={{
                ...useStyles.sidebarButton,
                background: 'rgba(34, 211, 238, 0.1)',
                borderColor: 'rgba(34, 211, 238, 0.3)',
                color: '#fafafa',
                '&:hover': {
                  background: 'rgba(34, 211, 238, 0.15)',
                  borderColor: '#22d3ee',
                  boxShadow: '0 0 20px rgba(34, 211, 238, 0.3), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(34, 211, 238, 0.05)',
                  borderColor: 'rgba(34, 211, 238, 0.1)',
                  color: 'rgba(250, 250, 250, 0.25)'
                },
                '& .MuiButton-startIcon': {
                  color: '#22d3ee'
                }
              }}
            >
              Upload to Gallery
            </Button>
          )}

          {/* Rename Button - For Gallery Only - Cyan Neon */}
          {bucketType === 'profile' && (
            <Button
              fullWidth
              variant="contained"
              startIcon={<EditIcon sx={{ color: '#22d3ee' }} />}
              onClick={handleRenameClick}
              disabled={selectedFiles.length !== 1}
              sx={{
                ...useStyles.sidebarButton,
                background: 'rgba(34, 211, 238, 0.1)',
                borderColor: 'rgba(34, 211, 238, 0.3)',
                color: '#fafafa',
                '&:hover': {
                  background: 'rgba(34, 211, 238, 0.15)',
                  borderColor: '#22d3ee',
                  boxShadow: '0 0 20px rgba(34, 211, 238, 0.3), inset 0 0 20px rgba(34, 211, 238, 0.1)',
                  transform: 'translateY(-2px)',
                },
                '&:disabled': {
                  background: 'rgba(34, 211, 238, 0.05)',
                  borderColor: 'rgba(34, 211, 238, 0.1)',
                  color: 'rgba(250, 250, 250, 0.25)'
                },
                '& .MuiButton-startIcon': {
                  color: '#22d3ee'
                }
              }}
            >
              Rename
            </Button>
          )}
          
          {/* Delete Button - Red Neon */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<DeleteIcon sx={{ color: '#f43f5e' }} />}
            onClick={handleDeleteClick}
            className="delete-button"
            disabled={selectedFiles.length === 0 || currentOperation}
            sx={{
              ...useStyles.sidebarButton,
              background: 'rgba(244, 63, 94, 0.1)',
              borderColor: 'rgba(244, 63, 94, 0.3)',
              color: '#fafafa',
              '&:hover': {
                background: 'rgba(244, 63, 94, 0.15)',
                borderColor: '#f43f5e',
                boxShadow: '0 0 20px rgba(244, 63, 94, 0.3), inset 0 0 20px rgba(244, 63, 94, 0.1)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: 'rgba(244, 63, 94, 0.05)',
                borderColor: 'rgba(244, 63, 94, 0.1)',
                color: 'rgba(250, 250, 250, 0.25)'
              },
              '& .MuiButton-startIcon': {
                color: '#f43f5e'
              }
            }}
          >
            Delete
          </Button>
          
          {/* Timelapse Button - Green Neon */}
          <Button
            fullWidth
            variant="contained"
            startIcon={<VideoLibraryIcon sx={{ color: '#34d399' }} />}
            onClick={handleTimelapseClick}
            className="timelapse-button"
            sx={{
              ...useStyles.sidebarButton,
              background: 'rgba(52, 211, 153, 0.1)',
              borderColor: 'rgba(52, 211, 153, 0.3)',
              color: '#fafafa',
              '&:hover': {
                background: 'rgba(52, 211, 153, 0.15)',
                borderColor: '#34d399',
                boxShadow: '0 0 20px rgba(52, 211, 153, 0.3), inset 0 0 20px rgba(52, 211, 153, 0.1)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                background: 'rgba(52, 211, 153, 0.05)',
                borderColor: 'rgba(52, 211, 153, 0.1)',
                color: 'rgba(250, 250, 250, 0.25)'
              },
              '& .MuiButton-startIcon': {
                color: '#34d399'
              }
            }}
          >
            Create Timelapse
          </Button>

          {/* ===== Canvas Groups Section - Neon Theme ===== */}
          {/* Show only in Live bucket when groups exist */}
          {bucketType === 'live' && Object.keys(fileGroups).length > 1 && (
            <Box sx={{ mt: 3, mb: 2 }}>
              {/* Section Divider - Gradient fade */}
              <Box sx={{
                my: 2,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.08), transparent)'
              }} />

              {/* Section Header */}
              <Typography
                variant="overline"
                sx={{
                  px: 1,
                  color: '#71717a',
                  fontWeight: 600,
                  letterSpacing: 2,
                  display: 'block',
                  mb: 1,
                  fontSize: '0.65rem'
                }}
              >
                Canvas Groups
              </Typography>

              {/* Groups List */}
              <List sx={{ py: 0 }}>
                {/* "All Files" option - always shown first */}
                <ListItem
                  button
                  selected={selectedGroup === 'all'}
                  onClick={() => {
                    setSelectedGroup('all');
                  }}
                  sx={{
                    borderRadius: '6px',
                    mb: 0.5,
                    py: 1,
                    transition: 'all 0.3s ease',
                    border: '1px solid transparent',
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(34, 211, 238, 0.05)',
                      borderColor: 'rgba(34, 211, 238, 0.3)',
                      boxShadow: '0 0 15px rgba(34, 211, 238, 0.1)',
                      '&:hover': {
                        backgroundColor: 'rgba(34, 211, 238, 0.1)',
                      }
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(39, 39, 42, 0.5)',
                      borderColor: 'rgba(255, 255, 255, 0.08)',
                    }
                  }}
                >
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontWeight={selectedGroup === 'all' ? 500 : 400} sx={{ fontSize: '0.85rem' }}>
                        All Files
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" sx={{ color: '#71717a', fontSize: '0.7rem' }}>
                        {files.length} file{files.length !== 1 ? 's' : ''}
                      </Typography>
                    }
                  />
                </ListItem>

                <Box sx={{ my: 1, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.06), transparent)' }} />

                {/* Individual group items - Neon glow style */}
                {Object.keys(fileGroups)
                  .sort()
                  .filter(groupName => fileGroups[groupName].length > 0)
                  .map(groupName => {
                    const fileCount = fileGroups[groupName].length;
                    const color = groupColors[groupName] || '#22d3ee';
                    const isSelected = selectedGroup === groupName;

                    return (
                      <ListItem
                        key={groupName}
                        button
                        selected={isSelected}
                        onClick={() => {
                          setSelectedGroup(groupName);
                        }}
                        sx={{
                          borderRadius: '6px',
                          mb: 0.5,
                          py: 1,
                          transition: 'all 0.3s ease',
                          border: '1px solid transparent',
                          '&.Mui-selected': {
                            backgroundColor: `${color}10`,
                            borderColor: `${color}50`,
                            boxShadow: `0 0 15px ${color}20`,
                            '&:hover': {
                              backgroundColor: `${color}15`,
                            }
                          },
                          '&:hover': {
                            backgroundColor: 'rgba(39, 39, 42, 0.5)',
                            borderColor: 'rgba(255, 255, 255, 0.08)',
                          }
                        }}
                      >
                        {/* Color dot indicator with glow */}
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            backgroundColor: color,
                            mr: 1.5,
                            flexShrink: 0,
                            boxShadow: `0 0 10px ${color}`,
                          }}
                        />

                        {/* Group name and file count */}
                        <ListItemText
                          primary={
                            <Typography
                              variant="body2"
                              fontWeight={isSelected ? 500 : 400}
                              sx={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '0.85rem',
                              }}
                            >
                              {groupName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" sx={{ color: '#71717a', fontSize: '0.7rem' }}>
                              {fileCount} file{fileCount !== 1 ? 's' : ''}
                            </Typography>
                          }
                        />

                        {/* File count chip - subtle neon */}
                        <Chip
                          label={fileCount}
                          size="small"
                          sx={{
                            ml: 1,
                            height: 18,
                            fontSize: '0.65rem',
                            fontWeight: 500,
                            backgroundColor: 'transparent',
                            color: color,
                            border: `1px solid ${color}40`,
                            '& .MuiChip-label': {
                              px: 1,
                            }
                          }}
                        />
                      </ListItem>
                    );
                  })}
              </List>
            </Box>
          )}
            </>
          ) : (
            // Collapsed view - icon strip
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
              <Tooltip title="Expand sidebar" arrow placement="right">
                <IconButton onClick={() => setLeftSidebarOpen(true)} sx={{ color: 'text.secondary' }}>
                  <MenuIcon />
                </IconButton>
              </Tooltip>

              {/* Refresh - Cyan/Teal */}
              <Tooltip
                title="Refresh"
                arrow
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#6BB5B0',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      '& .MuiTooltip-arrow': {
                        color: '#6BB5B0',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  onClick={() => fetchContents(currentPath, bucketType)}
                  sx={{
                    color: '#6BB5B0',
                    opacity: 0.9,
                    '&:hover': {
                      color: '#81C9C4',
                      opacity: 1,
                      backgroundColor: 'rgba(107, 181, 176, 0.15)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <RefreshIcon />
                </IconButton>
              </Tooltip>

              {/* Create Folder - Red */}
              <Tooltip
                title="Create folder"
                arrow
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#D63235',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      '& .MuiTooltip-arrow': {
                        color: '#D63235',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  onClick={() => setCreateFolderDialogOpen(true)}
                  sx={{
                    color: '#D63235',
                    opacity: 0.9,
                    '&:hover': {
                      color: '#ED4245',
                      opacity: 1,
                      backgroundColor: 'rgba(214, 50, 53, 0.15)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <FolderIcon />
                </IconButton>
              </Tooltip>

              {/* Move to Artwork - Only in Live bucket - Red */}
              {bucketType === 'live' && (
                <Tooltip
                  title="Move to artwork"
                  arrow
                  placement="right"
                  componentsProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#D63235',
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        '& .MuiTooltip-arrow': {
                          color: '#D63235',
                        },
                      },
                    },
                  }}
                >
                  <IconButton
                    onClick={handleMoveClick}
                    disabled={selectedFiles.length === 0}
                    sx={{
                      color: '#D63235',
                      opacity: selectedFiles.length === 0 ? 0.3 : 0.9,
                      '&:hover': {
                        color: '#ED4245',
                        opacity: 1,
                        backgroundColor: 'rgba(214, 50, 53, 0.15)',
                        transform: 'scale(1.1)',
                      },
                      '&:disabled': {
                        color: '#D63235',
                        opacity: 0.3,
                        transform: 'none',
                      },
                      transition: 'all 0.2s ease',
                    }}
                  >
                    <MoveIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* Delete - Purple */}
              <Tooltip
                title="Delete"
                arrow
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#673AB7',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      '& .MuiTooltip-arrow': {
                        color: '#673AB7',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  onClick={handleDeleteClick}
                  disabled={selectedFiles.length === 0}
                  sx={{
                    color: '#673AB7',
                    opacity: selectedFiles.length === 0 ? 0.3 : 0.9,
                    '&:hover': {
                      color: '#9C27B0',
                      opacity: 1,
                      backgroundColor: 'rgba(103, 58, 183, 0.15)',
                      transform: 'scale(1.1)',
                    },
                    '&:disabled': {
                      color: '#673AB7',
                      opacity: 0.3,
                      transform: 'none',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>

              {/* Create Timelapse - Green */}
              <Tooltip
                title="Create timelapse"
                arrow
                placement="right"
                componentsProps={{
                  tooltip: {
                    sx: {
                      bgcolor: '#3CA374',
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      '& .MuiTooltip-arrow': {
                        color: '#3CA374',
                      },
                    },
                  },
                }}
              >
                <IconButton
                  onClick={handleTimelapseClick}
                  sx={{
                    color: '#3CA374',
                    opacity: 0.9,
                    '&:hover': {
                      color: '#43B581',
                      opacity: 1,
                      backgroundColor: 'rgba(60, 163, 116, 0.15)',
                      transform: 'scale(1.1)',
                    },
                    transition: 'all 0.2s ease',
                  }}
                >
                  <VideoLibraryIcon />
                </IconButton>
              </Tooltip>

              {/* Canvas Groups - Colored Dots (October 2025) */}
              {/* Show only in Live bucket when groups exist */}
              {bucketType === 'live' && Object.keys(fileGroups).length > 1 && (
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
                  {/* Divider line above groups */}
                  <Box sx={{ width: '60%', height: 1, backgroundColor: 'rgba(255, 255, 255, 0.1)' }} />

                  {/* "All Files" dot */}
                  <Tooltip title="All Files" arrow placement="right">
                    <IconButton
                      onClick={() => setSelectedGroup('all')}
                      sx={{
                        p: 0.5,
                        width: 32,
                        height: 32,
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          backgroundColor: selectedGroup === 'all' ? '#FFFFFF' : 'rgba(255, 255, 255, 0.3)',
                          border: selectedGroup === 'all' ? '2px solid #FFFFFF' : '2px solid rgba(255, 255, 255, 0.3)',
                          boxShadow: selectedGroup === 'all' ? '0 0 8px rgba(255, 255, 255, 0.5)' : 'none',
                          transition: 'all 0.2s ease',
                        }}
                      />
                    </IconButton>
                  </Tooltip>

                  {/* Group dots */}
                  {Object.entries(fileGroups)
                    .filter(([groupName]) => groupName !== 'ungrouped')
                    .sort(([, a], [, b]) => b.length - a.length)
                    .map(([groupName, files]) => {
                      const color = groupColors[groupName] || '#FFFFFF';
                      const fileCount = files.length;
                      const isSelected = selectedGroup === groupName;

                      return (
                        <Tooltip
                          key={groupName}
                          title={
                            <Box sx={{ textAlign: 'center' }}>
                              <Typography variant="body2" fontWeight={600}>
                                {groupName}
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                                {fileCount} file{fileCount !== 1 ? 's' : ''}
                              </Typography>
                            </Box>
                          }
                          arrow
                          placement="right"
                        >
                          <IconButton
                            onClick={() => setSelectedGroup(groupName)}
                            sx={{
                              p: 0.5,
                              width: 32,
                              height: 32,
                              '&:hover': {
                                backgroundColor: `${color}20`,
                              }
                            }}
                          >
                            <Box
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: '50%',
                                backgroundColor: color,
                                border: isSelected ? `2px solid ${color}` : `2px solid ${color}80`,
                                boxShadow: isSelected ? `0 0 12px ${color}80` : `0 0 6px ${color}40`,
                                transition: 'all 0.2s ease',
                                transform: isSelected ? 'scale(1.15)' : 'scale(1)',
                              }}
                            />
                          </IconButton>
                        </Tooltip>
                      );
                    })}
                </Box>
              )}
            </Box>
          )}
        </Box>

        {/* File Grid or Timeline View (October 2025) */}
        <Box
          className="file-grid"
          sx={useStyles.fileGrid}
        >
          {viewMode === 'list' ? (
            <FileGrid
            files={getFilteredFiles()}
            folders={folders}
            selectedFiles={selectedFiles}
            loading={loading}
            warningAcknowledged={warningAcknowledged}
            setWarningAcknowledged={setWarningAcknowledged}
            sortConfig={sortConfig}
            onSort={handleSort}
            bucketType={bucketType}
            selectedGroup={selectedGroup}
            onSelect={(item, event) => {
              if (currentOperation) return; // Prevent selection during operations
              
              // For the first selection, we need a small delay to ensure React state has settled
              const isFirstSelection = selectedFiles.length === 0;
              
              // Handle shift + click
              if (event?.shiftKey && lastSelectedItem) {
                const allItems = [...folders, ...files];
                const lastIndex = allItems.findIndex(i => i.path === lastSelectedItem.path);
                const currentIndex = allItems.findIndex(i => i.path === item.path);
                
                if (lastIndex !== -1 && currentIndex !== -1) {
                  const start = Math.min(lastIndex, currentIndex);
                  const end = Math.max(lastIndex, currentIndex);
                  const itemsToSelect = allItems.slice(start, end + 1);
                  
                  // Create a new selection that keeps existing selections
                  const existingSelection = selectedFiles.filter(f => 
                    !itemsToSelect.find(i => i.path === f.path)
                  );
                  
                  // Merge existing selection with new items
                  setSelectedFiles([...existingSelection, ...itemsToSelect]);
                  
                  // Update preview if needed
                  if (item.type !== 'folder' && !previewFile) {
                    setPreviewFile(item);
                  }
                }
              } else {
                // Handle normal click (toggle selection)
                const alreadySelected = selectedFiles.some(
                  selected => selected.path === item.path
                );
                
                if (alreadySelected) {
                  // Create a new array to ensure state updates properly
                  const newSelection = selectedFiles.filter(selected => selected.path !== item.path);
                  setSelectedFiles(newSelection);
                  if (previewFile?.path === item.path) {
                    setPreviewFile(null);
                  }
                } else {
                  // For the first selection, use requestAnimationFrame to ensure state is stable
                  if (isFirstSelection) {
                    // Create a clone to ensure we're working with a fresh object
                    const itemClone = {...item};
                    requestAnimationFrame(() => {
                      setSelectedFiles([itemClone]);
                      if (itemClone.type !== 'folder') {
                        setPreviewFile(itemClone);
                      }
                    });
                  } else {
                    // For subsequent selections, normal behavior
                    setSelectedFiles([...selectedFiles, item]);
                    if (item.type !== 'folder') {
                      setPreviewFile(item);
                    }
                  }
                }
              }
              
              // Always update last selected item
              setLastSelectedItem(item);
            }}
            onFolderClick={(folder, event) => {
              // console.log("Folder clicked:", folder, "Current bucket:", bucketType); // Enhanced debug log
              if (event) {
                event.stopPropagation();
              }
              // Use a local reference to bucketType to ensure we're using the current value
              const currentBucket = bucketType;
              // Explicitly pass both folder path and current bucket
              fetchContents(folder.path, currentBucket);
            }}
            allSelected={selectedFiles.length === (getFilteredFiles().length + folders.length)}
            onSelectAll={(e) => {
              if (currentOperation) return; // Prevent selection during operations
              if (e.target.checked) {
                setSelectedFiles([...folders, ...getFilteredFiles()]);
              } else {
                setSelectedFiles([]);
                setPreviewFile(null);
              }
            }}
          />
          ) : (
            <CanvasTimelineView
              files={getFilteredFiles()}
              selectedFiles={selectedFiles}
              selectedGroup={selectedGroup}
              onSelectAll={(e) => {
                if (currentOperation) return; // Prevent selection during operations
                if (e.target.checked) {
                  setSelectedFiles(getFilteredFiles());
                } else {
                  setSelectedFiles([]);
                  setPreviewFile(null);
                }
              }}
              onFileSelect={(fileIdentifier, isCtrlClick, isShiftClick) => {
                if (currentOperation) return; // Prevent selection during operations

                // Find the file object from the identifier (path, key, or name)
                const file = getFilteredFiles().find(f =>
                  (f.path || f.key || f.name) === fileIdentifier
                );
                if (!file) return;

                const fileId = file.path || file.key || file.name;

                // Handle shift + click (range selection)
                if (isShiftClick && lastSelectedItem) {
                  const allFiles = getFilteredFiles();
                  const lastId = lastSelectedItem.path || lastSelectedItem.key || lastSelectedItem.name;
                  const lastIndex = allFiles.findIndex(f => (f.path || f.key || f.name) === lastId);
                  const currentIndex = allFiles.findIndex(f => (f.path || f.key || f.name) === fileId);

                  if (lastIndex !== -1 && currentIndex !== -1) {
                    const start = Math.min(lastIndex, currentIndex);
                    const end = Math.max(lastIndex, currentIndex);
                    const filesToSelect = allFiles.slice(start, end + 1);

                    // Keep existing selections
                    const existingSelection = selectedFiles.filter(f =>
                      !filesToSelect.find(item => {
                        const itemId = item.path || item.key || item.name;
                        const selectedId = f.path || f.key || f.name;
                        return itemId === selectedId;
                      })
                    );

                    setSelectedFiles([...existingSelection, ...filesToSelect]);
                    setPreviewFile(file);
                  }
                } else {
                  // Normal click or Ctrl+click
                  const alreadySelected = selectedFiles.some(
                    selected => (selected.path || selected.key || selected.name) === fileId
                  );

                  if (isCtrlClick) {
                    // Multi-select mode - toggle selection
                    if (alreadySelected) {
                      setSelectedFiles(selectedFiles.filter(
                        selected => (selected.path || selected.key || selected.name) !== fileId
                      ));
                      const previewId = previewFile?.path || previewFile?.key || previewFile?.name;
                      if (previewId === fileId) setPreviewFile(null);
                    } else {
                      setSelectedFiles([...selectedFiles, file]);
                      setPreviewFile(file);
                    }
                  } else {
                    // Single-select mode - select only this file
                    setSelectedFiles([file]);
                    setPreviewFile(file);
                  }
                }

                setLastSelectedItem(file);
              }}
              onFileClick={(file) => {
                // Double-click handler - open preview
                setPreviewFile(file);
              }}
              groupColors={groupColors}
              bucketType={bucketType}
            />
          )}
        </Box>

        {/* Preview Panel */}
        <Box
          className="preview-panel"
          sx={{
            position: { xs: 'relative', lg: 'absolute' },
            right: { xs: 'auto', lg: 0 },
            top: { xs: 'auto', lg: 0 },
            width: { xs: '100%', sm: '100%', lg: '40%' },
            p: { xs: 1.5, sm: 2 },
            borderLeft: { xs: 0, lg: '1px solid rgba(255, 255, 255, 0.05) !important' },
            borderTop: { xs: '1px solid rgba(255, 255, 255, 0.05) !important', lg: 0 },
            display: { xs: 'none', sm: 'none', lg: 'flex' }, // Hidden on mobile and tablet, visible on large screens
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'space-between',
            height: { xs: 'auto', lg: '100%' },
            backgroundColor: 'background.paper',
            boxShadow: { xs: '0px -2px 4px rgba(0,0,0,0.1)', lg: 'none' },
            zIndex: 1,
          }}
        >
          {/* Preview header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, letterSpacing: 1.2, fontSize: '0.7rem' }}>
              Preview
            </Typography>
          </Box>

          <Box sx={{
            flex: 1,
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 'auto',
            minHeight: { xs: '250px', sm: '300px', md: '400px' }
          }}>
            {previewFile ? (
              previewFile.path.endsWith('.mp4') ? (
                <VideoPreview
                  file={previewFile}
                  token={getAuthToken()}
                />
              ) : previewFile.path.endsWith('.supr') ? (
                <DeltaPreview
                  file={previewFile}
                  token={getAuthToken()}
                  onBlobLoaded={setPreviewBlob}
                />
              ) : (
                <JsonPreview
                  file={previewFile}
                  token={getAuthToken()}
                  onBlobLoaded={setPreviewBlob}
                />
              )
            ) : (
              <Typography color="text.secondary">
                Select a file to preview
              </Typography>
            )}
          </Box>
          <Button
            fullWidth
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            disabled={selectedFiles.length === 0 || currentOperation}
            sx={{ mt: 1 }}
          >
            Download {selectedFiles.length > 1 ? `(${selectedFiles.length} files)` : ''}
          </Button>
        </Box>

        {/* Mobile Preview - only shown on mobile when a file is selected */}
        <Box sx={{
          display: { xs: 'flex', md: 'none' }, // Always render on mobile, control visibility inside
          width: '100%',
          height: previewFile ? '300px' : '0px',
          overflow: 'hidden',
          p: previewFile ? 2 : 0,
          borderTop: previewFile ? 1 : 0,
          borderColor: 'rgba(255, 255, 255, 0.05)',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.3s ease',
          backgroundColor: previewFile ? 'background.paper' : 'transparent',
          boxShadow: previewFile ? '0px -4px 10px rgba(0, 0, 0, 0.15)' : 'none'
        }}>
          {previewFile ? (
            <>
              <Box sx={{ 
                width: '100%', 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                mb: 1
              }}>
                <Typography 
                  variant="subtitle1" 
                  fontWeight={600}
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 'calc(100% - 40px)' // Leave space for close button
                  }}
                >
                  {(previewFile.path.endsWith('.json') || previewFile.path.endsWith('.suprs'))
                    ? getDocumentName(previewFile)
                    : previewFile.name}
                </Typography>
                <IconButton 
                  size="small" 
                  onClick={() => setPreviewFile(null)}
                  sx={{ 
                    color: 'text.secondary',
                    ml: 1
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              <Box sx={{ 
                flex: 1, 
                width: '100%', 
                display: 'flex', 
                justifyContent: 'center', 
                alignItems: 'center',
                overflow: 'hidden', // Prevent content overflow
                borderRadius: 1,
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              }}>
                {/* Only mount preview components on small screens to prevent dual rendering */}
                {!isLargeScreen && previewFile.path.endsWith('.mp4') ? (
                  <VideoPreview
                    file={previewFile}
                    token={getAuthToken()}
                  />
                ) : !isLargeScreen && previewFile.path.endsWith('.supr') ? (
                  <DeltaPreview
                    file={previewFile}
                    token={getAuthToken()}
                    onBlobLoaded={setPreviewBlob}
                  />
                ) : !isLargeScreen ? (
                  <JsonPreview
                    file={previewFile}
                    token={getAuthToken()}
                    onBlobLoaded={setPreviewBlob}
                  />
                ) : null}
              </Box>

              <Button
                fullWidth
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={handleDownload}
                disabled={selectedFiles.length === 0 || currentOperation}
                sx={{
                  mt: 2,
                  borderRadius: 2,
                  height: 40,
                  boxShadow: 2,
                  background: 'linear-gradient(45deg, #6BB5B0 0%, #81C9C4 100%)',
                  color: '#1A1D23',
                  fontWeight: 600,
                  '&:hover': {
                    background: 'linear-gradient(45deg, #5CA5A0 0%, #A0DDD9 100%)',
                  },
                  '&:disabled': {
                    opacity: 0.6,
                  }
                }}
              >
                Download
              </Button>
            </>
          ) : null}
        </Box>
      </Box>

      {/* Bottom Status Bar - Full Width */}
      <Box
        sx={{
          height: '40px',
          borderTop: 1,
          borderColor: 'rgba(255, 255, 255, 0.05)',
          backgroundColor: 'background.paper',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          flexShrink: 0,
        }}
      >
        {/* Left Side: File count + Help Icons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {files.length} {files.length === 1 ? 'file' : 'files'}
            {selectedFiles.length > 0 && ` • ${selectedFiles.length} selected`}
          </Typography>

          <Box sx={{ width: 1, height: 20, borderLeft: '1px solid', borderColor: 'rgba(255, 255, 255, 0.05)', mx: 1 }} />

          {/* Help Icons */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {/* Guided Tour Icon Button */}
            <Tooltip
              title="Start Interactive Tour"
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#1E88E5',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    '& .MuiTooltip-arrow': {
                      color: '#1E88E5',
                    },
                  },
                },
              }}
            >
              <IconButton
                onClick={handleStartTour}
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  color: '#1E88E5',
                  '&:hover': {
                    color: '#1976D2',
                    backgroundColor: 'rgba(30, 136, 229, 0.1)',
                  }
                }}
              >
                <InfoIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            {/* How-To Guide Icon Button */}
            <Tooltip
              title="Open Help Documentation"
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#FF9800',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    '& .MuiTooltip-arrow': {
                      color: '#FF9800',
                    },
                  },
                },
              }}
            >
              <IconButton
                component="a"
                href="https://faq.suprsketch.com/suprdrive"
                target="_blank"
                rel="noopener noreferrer"
                size="small"
                sx={{
                  width: 28,
                  height: 28,
                  color: '#FF9800',
                  '&:hover': {
                    color: '#F57C00',
                    backgroundColor: 'rgba(255, 152, 0, 0.1)',
                  }
                }}
              >
                <HelpOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Right Side: Version */}
        <Typography variant="caption" color="text.secondary">
          SuprDrive v4.1.0
        </Typography>
      </Box>

      {/* Timelapse Folder Selection Dialog */}
        <Dialog
          open={timelapseDialogOpen}
          onClose={() => {
            if (!timelapseLoading) {
              setTimelapseDialogOpen(false);
              setSelectedTimelapseFolder(null);
              setShowTimelapseSettings(false);
              setGeneratedVideo(null);
            }
          }}
          maxWidth="sm"
          fullWidth
          TransitionComponent={Fade}
          PaperProps={{
            elevation: 2,
            sx: { 
              borderRadius: 2,
              backgroundColor: 'background.paper',
              backgroundImage: 'none',
              overflow: 'hidden'
            }
          }}
        >
          <DialogTitle sx={{
            py: 1.5,
            px: 2.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: 1,
            borderColor: 'rgba(255, 255, 255, 0.05)',
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <VideoLibraryIcon sx={{ color: 'success.main' }} />
              <Typography variant="h6" component="div">
                Create Timelapse
              </Typography>
            </Box>
            <IconButton 
              onClick={() => {
                if (!timelapseLoading) {
                  setTimelapseDialogOpen(false);
                  setSelectedTimelapseFolder(null);
                  setShowTimelapseSettings(false);
                  setGeneratedVideo(null);
                }
              }}
              size="small" 
              sx={{ 
                color: 'text.secondary',
                transition: 'all 0.2s',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  transform: 'rotate(90deg)',
                }
              }}
              aria-label="close"
              disabled={timelapseLoading}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ py: 2, px: 2.5 }}>
            <Box sx={{ mt: 0.5 }}>
              {/* Initial Folder Selection */}
              {!generatedVideo && !selectedTimelapseFolder && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ 
                    mb: 2,
                    color: 'success.main',
                    fontWeight: 600
                  }}>
                    Select Folder
                  </Typography>
                  {artworkFolders.map((folder) => (
                    <Button
                      key={folder.path}
                      fullWidth
                      disabled={timelapseLoading}
                      onClick={async () => {
                        setTimelapseLoading(true);
                        setSelectedTimelapseFolder(folder); // Show folder immediately
                        try {
                          const response = await axios.get('https://drive.suprsketch.com/api/fs/list', {
                            withCredentials: true, // Send cookies automatically (Unified Auth System)
                            params: { path: folder.path, bucket: 'artwork' }
                          });
                          // Count snapshots and deltas separately
                          const snapshots = response.data.files.filter(f => f.name.endsWith('.json') || f.name.endsWith('.suprs'));
                          const deltas = response.data.files.filter(f => f.name.endsWith('.supr'));
                          setTimelapseSnapshotCount(snapshots.length);
                          setTimelapseDeltaCount(deltas.length);
                          setTimelapseFileCount(snapshots.length + deltas.length);
                          setShowTimelapseSettings(true);
                        } finally {
                          setTimelapseLoading(false);
                        }
                      }}
                      sx={{
                        justifyContent: 'flex-start',
                        mb: 1,
                        p: 2,
                        bgcolor: 'rgba(255, 255, 255, 0.03)',
                        borderRadius: '10px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: 'rgba(255, 255, 255, 0.08)',
                          transform: 'translateY(-2px)',
                          boxShadow: 2
                        }
                      }}
                    >
                      {timelapseLoading && selectedTimelapseFolder?.path === folder.path ? (
                        <CircularProgress size={20} sx={{ mr: 1, color: 'success.main' }} />
                      ) : (
                        <FolderIcon sx={{ mr: 1, color: 'success.main' }} />
                      )}
                      {folder.name}
                    </Button>
                  ))}
                </Box>
              )}

              {/* Settings Section - Only show if selected and not generated */}
              {!generatedVideo && selectedTimelapseFolder && showTimelapseSettings && (
                <Box sx={{ mb: 2 }}>
                  {/* Beta Notice */}
                  <Alert severity="info" sx={{ mb: 2, fontSize: '0.8rem' }}>
                    Timelapse generation is currently in beta. Please report any issues.
                  </Alert>

                  {/* Folder Info */}
                  <Box sx={{
                    p: 2,
                    borderRadius: '8px',
                    bgcolor: 'rgba(67, 181, 129, 0.08)',
                    mb: 2
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>
                      {selectedTimelapseFolder.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                      {timelapseSnapshotCount} snapshots, {timelapseDeltaCount} stroke deltas
                    </Typography>
                  </Box>

                  {/* Processing Method - FIRST */}
                  <Box sx={{ mb: 2 }}>
                    <FormControl component="fieldset" fullWidth>
                      <Typography sx={{ color: 'success.main', fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
                        Processing Method
                      </Typography>
                      <RadioGroup
                        value={timelapseSettings.processingMethod}
                        onChange={(e) => setTimelapseSettings(prev => ({
                          ...prev,
                          processingMethod: e.target.value
                        }))}
                      >
                        <FormControlLabel
                          value="browser"
                          control={<Radio sx={{ color: 'success.main', '&.Mui-checked': { color: 'success.main' } }} />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography sx={{ fontWeight: 500 }}>
                                Browser (Recommended) ⭐
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Fast processing using your computer's power. Supports up to 4K resolution.
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 1.5, alignItems: 'flex-start', py: 1 }}
                        />
                        <FormControlLabel
                          value="server"
                          disabled={true}
                          control={<Radio sx={{ color: 'text.disabled' }} />}
                          label={
                            <Box sx={{ ml: 1, opacity: 0.5 }}>
                              <Typography sx={{ fontWeight: 500, color: 'text.disabled' }}>
                                Server (Under Development)
                              </Typography>
                              <Typography variant="caption" color="text.disabled">
                                Cloud-based processing. Coming soon.
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* Frame Selection Strategy */}
                  <Box sx={{ mb: 2 }}>
                    <FormControl component="fieldset" fullWidth>
                      <Typography sx={{ color: 'success.main', fontWeight: 600, mb: 1.5, fontSize: '0.85rem' }}>
                        Frame Selection
                      </Typography>
                      <RadioGroup
                        value={timelapseSettings.frameSelection}
                        onChange={(e) => setTimelapseSettings(prev => ({
                          ...prev,
                          frameSelection: e.target.value
                        }))}
                      >
                        <FormControlLabel
                          value="include-strokes"
                          control={<Radio sx={{ color: 'success.main', '&.Mui-checked': { color: 'success.main' } }} />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography sx={{ fontWeight: 500 }}>
                                Include All Strokes ⭐
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {timelapseSnapshotCount + timelapseDeltaCount} frames • Complete drawing progression
                              </Typography>
                            </Box>
                          }
                          sx={{ mb: 1.5, alignItems: 'flex-start', py: 1 }}
                        />
                        <FormControlLabel
                          value="snapshots-only"
                          control={<Radio sx={{ color: 'success.main', '&.Mui-checked': { color: 'success.main' } }} />}
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography sx={{ fontWeight: 500 }}>
                                Snapshots Only
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {timelapseSnapshotCount} frames • Shows major progress points
                              </Typography>
                            </Box>
                          }
                          sx={{ alignItems: 'flex-start', py: 1 }}
                        />
                      </RadioGroup>
                    </FormControl>
                  </Box>

                  {/* FPS Control */}
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                      <Typography gutterBottom sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.85rem' }}>
                        Frames Per Second: {timelapseSettings.fps}
                      </Typography>
                      <Slider
                        value={timelapseSettings.fps}
                        onChange={(e, newValue) => setTimelapseSettings(prev => ({
                          ...prev,
                          fps: newValue
                        }))}
                        min={1}
                        max={60}
                        marks={[
                          { value: 1, label: '1' },
                          { value: 30, label: '30' },
                          { value: 60, label: '60' }
                        ]}
                        valueLabelDisplay="auto"
                        sx={{
                          color: 'success.main',
                          '& .MuiSlider-rail': {
                            height: 8,
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '& .MuiSlider-track': {
                            height: 8
                          }
                        }}
                      />
                    </FormControl>
                  </Box>

                  {/* Hold Last Frame Control */}
                  <Box sx={{ mb: 2 }}>
                    <FormControl fullWidth>
                      <Typography gutterBottom sx={{ color: 'success.main', fontWeight: 600, fontSize: '0.85rem' }}>
                        Hold Last Frame: {timelapseSettings.freezeDuration === 0 ? 'None' : `${timelapseSettings.freezeDuration} seconds`}
                      </Typography>
                      <Slider
                        value={timelapseSettings.freezeDuration}
                        onChange={(e, newValue) => setTimelapseSettings(prev => ({
                          ...prev,
                          freezeDuration: newValue
                        }))}
                        min={0}
                        max={10}
                        step={1}
                        marks={[
                          { value: 0, label: '0s' },
                          { value: 3, label: '3s' },
                          { value: 5, label: '5s' },
                          { value: 10, label: '10s' }
                        ]}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(value) => value === 0 ? 'None' : `${value}s`}
                        sx={{
                          color: 'success.main',
                          '& .MuiSlider-rail': {
                            height: 8,
                            bgcolor: 'rgba(255, 255, 255, 0.1)'
                          },
                          '& .MuiSlider-track': {
                            height: 8
                          }
                        }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                        Pause on the final artwork before the video ends
                      </Typography>
                    </FormControl>
                  </Box>

                  {/* Processing Info Summary */}
                  <Box sx={{
                    p: 1.5,
                    bgcolor: 'rgba(76, 175, 80, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(76, 175, 80, 0.3)'
                  }}>
                    {(() => {
                      const selectedFrameCount = timelapseSettings.frameSelection === 'snapshots-only'
                        ? timelapseSnapshotCount
                        : (timelapseSnapshotCount + timelapseDeltaCount);
                      const estimatedMinutes = timelapseSettings.processingMethod === 'browser'
                        ? Math.ceil(selectedFrameCount * 0.02)
                        : Math.ceil(selectedFrameCount * 0.035);  // Updated after PERF optimizations
                      return (
                        <>
                          <Typography variant="body2" sx={{ color: 'success.main', fontWeight: 600, mb: 0.5, fontSize: '0.8rem' }}>
                            {timelapseSettings.processingMethod === 'browser' ? 'Browser Processing' : 'Server Processing'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                            {timelapseSettings.processingMethod === 'browser'
                              ? `~${estimatedMinutes} minutes • Up to 4K resolution`
                              : `~${estimatedMinutes} minutes • Original resolution`}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                            {selectedFrameCount} frames at {timelapseSettings.fps} FPS = {((selectedFrameCount / timelapseSettings.fps) + timelapseSettings.freezeDuration).toFixed(1)}s video
                            {timelapseSettings.freezeDuration > 0 && ` (includes ${timelapseSettings.freezeDuration}s hold)`}
                          </Typography>
                        </>
                      );
                    })()}
                  </Box>
                </Box>
              )}

              {/* Progress Section */}
              {timelapseLoading && (
                <Box sx={{ width: '100%', mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {timelapseProgress.stage === 'collecting'
                      ? `Collecting files...${timelapseProgress.processed}/${timelapseProgress.total || '?'}`
                      : timelapseProgress.stage === 'processing'
                      ? `Processing frames: ${timelapseProgress.processed}/${timelapseProgress.total} (${Math.round((timelapseProgress.processed / timelapseProgress.total) * 100)}%)`
                      : timelapseProgress.stage === 'encoding'
                      ? `Encoding video: ${Math.round(timelapseProgress.progress)}%`
                      : 'Preparing...'}
                  </Typography>
                  <LinearProgress 
                    variant={timelapseProgress.progress !== undefined ? "determinate" : "indeterminate"}
                    value={
                      timelapseProgress.stage === 'encoding' 
                        ? timelapseProgress.progress
                        : timelapseProgress.stage === 'processing' && timelapseProgress.total
                        ? (timelapseProgress.processed / timelapseProgress.total) * 100
                        : 0
                    }
                  />
                </Box>
              )}

              {/* Success Message */}
              {generatedVideo && (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 2
                }}>
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexDirection: 'column',
                      mb: 3
                    }}
                  >
                    <VideoIcon 
                      sx={{ 
                        fontSize: 60, 
                        color: 'success.main', 
                        mb: 2 
                      }} 
                    />
                    <Typography variant="h6" gutterBottom>
                      Timelapse Generated Successfully!
                    </Typography>
                    <Typography color="text.secondary" variant="body2">
                      Your video is ready to view or download
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </DialogContent>

          {/* Action Buttons */}
          <DialogActions sx={{ 
            px: 3, 
            py: 2,
            borderTop: 1,
            borderColor: 'rgba(255, 255, 255, 0.05)',
          }}>
            {!timelapseLoading && !generatedVideo ? (
              <>
                <Button
                  onClick={() => {
                    setTimelapseDialogOpen(false);
                    setSelectedTimelapseFolder(null);
                    setShowTimelapseSettings(false);
                  }}
                  variant="outlined"
                  sx={{
                    px: 2,
                    borderRadius: 1.5,
                  }}
                >
                  Cancel
                </Button>
                {timelapseSettings.processingMethod === 'browser' ? (
                  <BrowserTimelapse
                    folderPath={selectedTimelapseFolder?.path}
                    bucketType="artwork"
                    disabled={!selectedTimelapseFolder}
                    fps={timelapseSettings.fps}
                    frameSelection={timelapseSettings.frameSelection}
                    freezeDuration={timelapseSettings.freezeDuration}
                    onComplete={(result) => {
                      console.log('Browser timelapse complete:', result);
                      setSnackbarMessage('Timelapse generated successfully!');
                      setShowSnackbar(true);
                    }}
                    onViewInVideoBucket={(videoDetails) => {
                      // Close the timelapse dialog and navigate to video bucket
                      setTimelapseDialogOpen(false);
                      navigateToVideo(videoDetails);
                    }}
                  />
                ) : (
                  <Button
                    variant="contained"
                    onClick={() => handleGenerateTimelapse(selectedTimelapseFolder.path)}
                    disabled={!selectedTimelapseFolder}
                    color="success"
                    sx={{
                      px: 3,
                      borderRadius: 1.5,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 3,
                        transform: 'translateY(-2px)',
                      },
                      transition: 'all 0.2s',
                    }}
                  >
                    Generate Timelapse
                  </Button>
                )}
              </>
            ) : timelapseLoading ? null : generatedVideo && (
              <>
                <Button
                  variant="outlined"
                  color="success"
                  onClick={() => {
                    navigateToVideo(generatedVideo);
                    setTimelapseDialogOpen(false);
                  }}
                  sx={{ 
                    px: 2,
                    borderRadius: 1.5,
                  }}
                >
                  Show in Folder
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => {
                    // Create a properly structured file object that includes the bucket
                    const videoFile = {
                      path: generatedVideo.key,
                      name: generatedVideo.name,
                      type: 'file',
                      bucket: 'video'  // Explicitly set the bucket
                    };
                    
                    // First close the dialog
                    setTimelapseDialogOpen(false);
                    
                    // Then download the video directly without changing the main UI selection
                    axios.get(
                      `https://drive.suprsketch.com/api/fs/download/${encodeURIComponent(videoFile.path)}`,
                      {
                        withCredentials: true, // Send cookies automatically (Unified Auth System)
                        params: { bucket: videoFile.bucket },
                        responseType: 'blob'
                      }
                    ).then(response => {
                      const url = window.URL.createObjectURL(new Blob([response.data]));
                      const link = document.createElement('a');
                      link.href = url;
                      link.setAttribute('download', videoFile.name);
                      document.body.appendChild(link);
                      link.click();
                      link.remove();
                      window.URL.revokeObjectURL(url);
                    }).catch(error => {
                      setSnackbarMessage('Download failed');
                      setShowSnackbar(true);
                    });
                  }}
                  sx={{ 
                    px: 3,
                    borderRadius: 1.5,
                    boxShadow: 2,
                    '&:hover': {
                      boxShadow: 3,
                      transform: 'translateY(-2px)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  Download
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Move Directory Selection Dialog */}
        <Dialog
          open={moveDialogOpen}
          onClose={() => !moveLoading && setMoveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {bucketType === 'live' ? 'Select Artwork Folder' : 'Select Destination Folder'}
          </DialogTitle>
          <DialogContent>
            {moveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {moveError}
              </Alert>
            )}
            
            <Box sx={{ mt: 2 }}>
              {/* Show all folders - removed root folder option for security */}
              {artworkContents.folders
                .filter(dir => bucketType === 'artwork' ? dir.path !== currentPath : true)
                .map((dir) => (
                  <Button
                    key={dir.path}
                    fullWidth
                    onClick={() => executeMove(dir.path)}
                    disabled={moveLoading || currentOperation}
                    sx={{
                      justifyContent: 'flex-start',
                      mb: 1,
                      textAlign: 'left',
                      color: 'text.primary'
                    }}
                  >
                    <FolderIcon sx={{ mr: 1 }} /> {dir.name}
                  </Button>
                ))}
              
              {/* Show message if no folders available */}
              {artworkContents.folders.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No folders available. Create a folder first.
                </Typography>
              )}
              
              {/* Show message if all folders are filtered out (current folder) */}
              {bucketType === 'artwork' && artworkContents.folders.length > 0 && 
               artworkContents.folders.filter(dir => dir.path !== currentPath).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No other folders available. Create a new folder or navigate to a different folder to move files.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setMoveDialogOpen(false)}
              disabled={moveLoading}
            >
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        {/* Create Folder Dialog */}
        <Dialog
          open={createFolderDialogOpen}
          onClose={() => setCreateFolderDialogOpen(false)}
          aria-labelledby="create-folder-dialog-title"
          disableEnforceFocus
          keepMounted={false}
        >
          <DialogTitle id="create-folder-dialog-title">Create New Folder</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Folder Name"
              fullWidth
              variant="standard"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              aria-label="Folder name input"
              id="folder-name-input"
              // Add helper text to explain the allowed characters
              helperText="Only letters, numbers, spaces, hyphens, and underscores are allowed."
              // Add error state if invalid characters are entered
              error={/[^a-zA-Z0-9\-_ ]/.test(newFolderName)}
              // Optionally add input validation to prevent entering invalid characters
              inputProps={{
                pattern: "[a-zA-Z0-9\\-_ ]+",
              }}
            />
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setCreateFolderDialogOpen(false)}
              aria-label="Cancel folder creation"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreateFolder}
              aria-label="Create folder"
              // Disable the button if the input contains invalid characters
              disabled={/[^a-zA-Z0-9\-_ ]/.test(newFolderName) || !newFolderName.trim()}
            >
              Create
            </Button>
          </DialogActions>
        </Dialog>

        {/* Operation Error Dialog */}
        <Dialog
          open={!!operationError}
          onClose={() => setOperationError(null)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Operation {operationError?.type || ''} Status
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              {operationError?.failed?.length 
                ? `Failed to process ${operationError.failed.length} items. ${operationError.completed} items were processed successfully.`
                : operationError?.message
              }
            </Alert>
            {operationError?.failed?.length > 0 && (
              <Box sx={{ mt: 2, maxHeight: 200, overflow: 'auto' }}>
                {operationError.failed.map((fail, index) => (
                  <Typography key={index} variant="body2" color="error">
                    {fail.file.name}: {fail.error}
                  </Typography>
                ))}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOperationError(null)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={showSnackbar}
          autoHideDuration={6000}
          onClose={() => setShowSnackbar(false)}
          message={snackbarMessage}
          action={
            <IconButton
              size="small"
              color="inherit"
              onClick={() => setShowSnackbar(false)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        />

        {/* Archive Confirmation Dialog */}
        <Dialog
          open={archiveDialogOpen}
          onClose={() => !archiveLoading && setArchiveDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Archive Folder
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Are you sure you want to archive the folder "{folderToArchive?.name}"?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Archived folders will be moved to the archive directory and hidden from the main view unless "Show Archived" is enabled.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setArchiveDialogOpen(false)}
              disabled={archiveLoading}
            >
              Cancel
            </Button>
            <Button 
              onClick={executeArchive}
              disabled={archiveLoading}
              variant="contained"
              startIcon={archiveLoading ? <CircularProgress size={20} /> : <ArchiveIcon />}
              sx={{
                background: 'linear-gradient(45deg, #95A5A6 0%, #BDC3C7 100%)',
                '&:hover': {
                  background: 'linear-gradient(45deg, #7F8C8D 0%, #95A5A6 100%)'
                }
              }}
            >
              Archive
            </Button>
          </DialogActions>
        </Dialog>

        {/* Rename Dialog */}
        <Dialog 
          open={renameDialogOpen}
          onClose={() => setRenameDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Rename File: {fileToRename?.name || ''}
          </DialogTitle>
          {fileToRename && (
            <form onSubmit={(e) => {
              e.preventDefault();
              // Get the base name without extension from the form
              const newBaseName = e.target.elements.newName.value;
              
              // Extract extension from original file name
              const match = fileToRename.name.match(/(\.[^.]+)$/);
              const extension = match ? match[1] : '';
              
              // Combine for the full new filename
              const fullNewName = extension ? `${newBaseName}${extension}` : newBaseName;
              
              handleRename(fullNewName);
            }}>
              <DialogContent>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <TextField
                    autoFocus
                    margin="dense"
                    name="newName"
                    label="New File Name"
                    type="text"
                    fullWidth
                    key={fileToRename.path} // Add a key based on the file to force re-render
                    defaultValue={fileToRename.name.replace(/\.[^.]+$/, '')} // Remove extension
                    disabled={renameLoading}
                  />
                  {fileToRename.name.match(/\.[^.]+$/) && (
                    <Typography sx={{ ml: 1, color: 'text.secondary', fontSize: '1rem' }}>
                      {fileToRename.name.match(/(\.[^.]+)$/)[1]} {/* Display extension */}
                    </Typography>
                  )}
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRenameDialogOpen(false)} disabled={renameLoading}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={renameLoading}
                  variant="contained"
                  startIcon={renameLoading ? <CircularProgress size={20} /> : null}
                >
                  Rename
                </Button>
              </DialogActions>
            </form>
          )}
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={deleteConfirmOpen}
          onClose={() => setDeleteConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            🗑️ Confirm Delete
          </DialogTitle>
          <DialogContent>
            <Alert severity="error" sx={{ mb: 2 }}>
              This action cannot be undone!
            </Alert>
            <Typography variant="body1" gutterBottom>
              You are about to permanently delete {itemsToDelete.length} {itemsToDelete.length === 1 ? 'item' : 'items'}.
            </Typography>
            <Box sx={{ 
              mt: 2, 
              p: 2, 
              bgcolor: 'background.paper',
              borderRadius: 1,
              maxHeight: 200,
              overflow: 'auto'
            }}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Files to be deleted:
              </Typography>
              {itemsToDelete.map((item, index) => (
                <Typography key={index} variant="body2" sx={{ ml: 2 }}>
                  • {item.type === 'folder' ? item.name : getDocumentName(item)} {item.type === 'folder' ? '(folder)' : ''}
                </Typography>
              ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteConfirmOpen(false)} 
              variant="outlined"
            >
              Cancel
            </Button>
            <Button 
              onClick={() => {
                handleDelete();
                setDeleteConfirmOpen(false);
              }} 
              color="error"
              variant="contained"
              autoFocus
            >
              Delete {itemsToDelete.length} {itemsToDelete.length === 1 ? 'Item' : 'Items'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Guided Tour */}
        <GuidedTour />
    </Box>
  );
};

export default Dashboard;

// Add GuidedTour to end of Dashboard component before the export